/**
 * Configuration Panel
 *
 * Left panel of the Home Layout Manager containing:
 * - Collapsible banner sections (top, slider, bottom)
 * - Draggable category list with visibility badges
 *
 * Uses the draft system - changes are stored in draft until published.
 *
 * @module HomeLayoutManager/components/ConfigurationPanel
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Collapse,
  Badge,
  Chip,
  Card,
  CardContent,
  Checkbox,
  Switch,
  Tooltip,
  Button,
  Skeleton,
  LinearProgress,
  alpha,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  CircularProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ViewCarousel as ViewCarouselIcon,
  Storefront as StorefrontIcon,
  Home as HomeIcon,
  Add as AddIcon,
  Image as ImageIcon,
  Category as CategoryIcon,
  FilterList as FilterListIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  TrendingUp as TrendingUpIcon,
  Info as InfoIcon,
  LocalOffer as BrandsIcon,
  Reorder as ReorderIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  FiberNew as NewIcon,
} from '@mui/icons-material';
import Logger from '../../../utils/Logger';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DraggableProvided,
  DraggableStateSnapshot,
  DroppableProvided,
  DroppableStateSnapshot,
} from '@hello-pangea/dnd';

import { useHomeLayout } from '../HomeLayoutContext';
import type {
  BannerPositionDraft,
  BannerItemDraft,
  BannerItemDraftSaveData,
  CategoryDraft,
  Brand,
  Store,
  HomeFilterType,
  NewPopularConfig,
} from '../types';
import { ALL_LOCATIONS } from '../types';
import { httpClient } from '../../../components/fetchApiData';
import config from '../../../config';
import { getValidToken } from '../../../services/tokenRefreshService';
import {
  fetchStores,
  fetchCategoryProductCount,
  fetchBrandProductCount,
  fetchAllProductCounts,
  clearBrandsCache,
  deleteBrand,
} from '../api';
import { useStoreVisibility } from '../../../hooks/useStoreVisibility';

// Import existing dialogs for reuse
import BannerItemFormDialog from '../../../components/BannerManagement/BannerItemFormDialog';
import AllCategoriesTab from './AllCategoriesTab';
import CategoryProductsModal from './CategoryProductsModal';
import BrandProductsModal from './BrandProductsModal';
import BrandFormDialog from './BrandFormDialog';

const API_BASE = `${config.API_URL}/admin`;

/**
 * Portal-aware wrapper for draggable items
 *
 * Renders dragged items at document.body level via React Portal to avoid
 * CSS transform/position issues from parent containers (admin sidebars, modals, etc.)
 *
 * This fixes the common "dragged item offset" issue in layouts with fixed sidebars.
 */
interface PortalAwareDraggableProps {
  /** Draggable provided props */
  provided: DraggableProvided;
  /** Draggable snapshot state */
  snapshot: DraggableStateSnapshot;
  /** Child elements */
  children: React.ReactNode;
}

function PortalAwareDraggable({
  provided,
  snapshot,
  children,
}: PortalAwareDraggableProps): React.ReactElement {
  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      style={{
        ...provided.draggableProps.style,
        marginBottom: '8px',
      }}
    >
      {children}
    </div>
  );
}

/**
 * Carousel Placeholder Card
 * Draggable card representing the carousel position in custom mode
 */
interface CarouselPlaceholderCardProps {
  isDragging: boolean;
  dragHandleProps: React.HTMLAttributes<HTMLDivElement> | null | undefined;
}

const CarouselPlaceholderCard = React.memo(function CarouselPlaceholderCard({
  isDragging,
  dragHandleProps,
}: CarouselPlaceholderCardProps): React.ReactElement {
  return (
    <Card
      variant="outlined"
      sx={{
        transition: isDragging ? 'none' : 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'grab',
        border: '2px dashed',
        borderColor: isDragging ? 'primary.main' : 'primary.light',
        background: isDragging
          ? alpha('#1976d2', 0.08)
          : `linear-gradient(135deg, ${alpha('#1976d2', 0.04)} 0%, ${alpha('#1976d2', 0.08)} 100%)`,
        '&:hover': isDragging ? {} : {
          borderColor: 'primary.main',
          boxShadow: '0 4px 16px rgba(25, 118, 210, 0.2)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Drag handle */}
          <Box
            {...dragHandleProps}
            sx={{
              cursor: isDragging ? 'grabbing' : 'grab',
              display: 'flex',
              alignItems: 'center',
              color: isDragging ? 'primary.main' : 'primary.light',
              transition: 'color 0.2s ease',
            }}
          >
            <DragIndicatorIcon />
          </Box>

          {/* Icon */}
          <Box
            sx={{
              width: 48,
              height: 48,
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ViewCarouselIcon sx={{ fontSize: 28, color: 'white' }} />
          </Box>

          {/* Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} color="primary.main">
              Category Carousel
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Drag to reposition among scrollers
            </Typography>
          </Box>

          {/* Visual indicator */}
          <Chip
            icon={<ViewCarouselIcon />}
            label="Draggable"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: 24 }}
          />
        </Box>
      </CardContent>
    </Card>
  );
});

/**
 * Brand Scroller Placeholder Card
 * Draggable card representing the brand scroller position in the home list
 */
interface BrandScrollerPlaceholderCardProps {
  isDragging: boolean;
  dragHandleProps: React.HTMLAttributes<HTMLDivElement> | null | undefined;
}

const BrandScrollerPlaceholderCard = React.memo(function BrandScrollerPlaceholderCard({
  isDragging,
  dragHandleProps,
}: BrandScrollerPlaceholderCardProps): React.ReactElement {
  return (
    <Card
      variant="outlined"
      sx={{
        transition: isDragging ? 'none' : 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'grab',
        border: '2px dashed',
        borderColor: isDragging ? 'secondary.main' : 'secondary.light',
        background: isDragging
          ? alpha('#9c27b0', 0.08)
          : `linear-gradient(135deg, ${alpha('#9c27b0', 0.04)} 0%, ${alpha('#9c27b0', 0.08)} 100%)`,
        '&:hover': isDragging ? {} : {
          borderColor: 'secondary.main',
          boxShadow: '0 4px 16px rgba(156, 39, 176, 0.2)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Drag handle */}
          <Box
            {...dragHandleProps}
            sx={{
              cursor: isDragging ? 'grabbing' : 'grab',
              display: 'flex',
              alignItems: 'center',
              color: isDragging ? 'secondary.main' : 'secondary.light',
              transition: 'color 0.2s ease',
            }}
          >
            <DragIndicatorIcon />
          </Box>

          {/* Icon */}
          <Box
            sx={{
              width: 48,
              height: 48,
              background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <BrandsIcon sx={{ fontSize: 28, color: 'white' }} />
          </Box>

          {/* Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} color="secondary.main">
              Brand Scroller
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Drag to reposition among scrollers
            </Typography>
          </Box>

          {/* Visual indicator */}
          <Chip
            icon={<BrandsIcon />}
            label="Draggable"
            size="small"
            color="secondary"
            variant="outlined"
            sx={{ height: 24 }}
          />
        </Box>
      </CardContent>
    </Card>
  );
});

/**
 * Brand logo plate — the shape Command Center's brand rail uses
 * (`command-center/app/brands/brands.module.css`, `.logoBox` / `.logo`):
 * a 92x40 WHITE plate with the logo contained inside it.
 *
 * It used to be a 32x32 grey square. Brand logos are wide wordmarks — the
 * legacy DEAR family images are 250x80 and the current app ones 500x500
 * greyscale — so a square that small rendered every wide logo as an unreadable
 * smear, in the one list whose whole job is deciding which brand goes where.
 * White rather than grey because the logos are transparent PNGs authored to sit
 * on the app's white tile; grey behind them just looked like a broken image.
 *
 * A missing logo is called out in red rather than shown as a neutral icon: a
 * brand with no image renders as a blank tile and is dropped from the app's
 * brand rail, so "no logo" is a fault to fix, not a neutral state.
 */
function BrandLogoPlate({ imageUrl, name }: { imageUrl?: string | null; name: string }): React.ReactElement {
  return (
    <Box
      sx={{
        width: 92,
        height: 40,
        flexShrink: 0,
        borderRadius: '6px',
        bgcolor: '#fff',
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={name}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      ) : (
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: '#b00',
          }}
        >
          none
        </Typography>
      )}
    </Box>
  );
}

/**
 * Converts homeFilterType enum to user-friendly label
 */
function getFilterTypeLabel(filterType: string): string {
  switch (filterType) {
    case 'category':
      return 'By Category';
    case 'newOrPopular':
      return 'New & Popular';
    case 'tag':
      return 'By Tag';
    case 'brand':
      return 'By Brand';
    case 'combined':
      return 'Combined Filter';
    case 'none':
      return 'Icon Only';
    default:
      return filterType;
  }
}

/**
 * Banner section header with capacity indicator
 */
interface BannerSectionHeaderProps {
  /** Position configuration */
  position: BannerPositionDraft;
  /** Whether section is expanded */
  expanded: boolean;
  /** Toggle expansion callback */
  onToggle: () => void;
}

function BannerSectionHeader({
  position,
  expanded,
  onToggle,
}: BannerSectionHeaderProps): React.ReactElement {
  const activeCount = position.items.filter((item) => item.isActive).length;
  const totalCount = position.items.length;

  return (
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1.5,
        cursor: 'pointer',
        borderRadius: 1,
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <ImageIcon color="action" fontSize="small" />
        <Typography variant="subtitle2" fontWeight={600}>
          {position.label}
        </Typography>
        <Chip
          label={`${activeCount}/${position.maxItems}`}
          size="small"
          color={activeCount > 0 ? 'primary' : 'default'}
          variant="outlined"
        />
      </Box>
      <IconButton size="small">
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
    </Box>
  );
}

/**
 * Banner item card for the collapsed list
 */
interface BannerItemCardProps {
  /** Banner item data */
  item: BannerItemDraft;
  /** Edit callback */
  onEdit: () => void;
  /** Toggle active callback */
  onToggleActive: () => void;
  /** Delete callback */
  onDelete: () => void;
  /** Drag handle props for reordering */
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement> | null;
  /** Whether the item is being dragged */
  isDragging?: boolean;
}

function BannerItemCard({
  item,
  onEdit,
  onToggleActive,
  onDelete,
  dragHandleProps,
  isDragging,
}: BannerItemCardProps): React.ReactElement {
  const isNew = item.id.startsWith('draft_');
  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        mb: 1,
        opacity: item.isActive ? 1 : 0.6,
        cursor: 'pointer',
        transition: isDragging ? 'none' : 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        borderColor: isDragging ? 'primary.main' : isNew ? 'success.main' : undefined,
        boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.15)' : undefined,
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: alpha('#1976d2', 0.04),
          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.08)',
          transform: isDragging ? 'none' : 'translateY(-1px)',
        },
        '&:active': {
          transform: 'translateY(0)',
        },
      }}
      onClick={onEdit}
    >
      {dragHandleProps && (
        <Box
          {...dragHandleProps}
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            color: 'text.secondary',
            '&:hover': { color: 'primary.main' },
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
      )}
      <Box
        sx={{
          width: 48,
          height: 32,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'grey.200',
          flexShrink: 0,
        }}
      >
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ImageIcon fontSize="small" color="disabled" />
          </Box>
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {item.name || 'Untitled'}
        </Typography>
      </Box>
      {isNew && (
        <Chip
          icon={<NewIcon sx={{ fontSize: 14 }} />}
          label="NEW"
          size="small"
          color="success"
          variant="outlined"
          sx={{ height: 22, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
        />
      )}
      <Tooltip title={item.isActive ? 'Deactivate' : 'Activate'}>
        <Switch
          size="small"
          checked={item.isActive}
          onChange={(e) => {
            e.stopPropagation();
            onToggleActive();
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </Tooltip>
      <Tooltip title="Edit">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          sx={{
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: alpha('#1976d2', 0.1),
              transform: 'scale(1.1)',
            },
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          sx={{
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: alpha('#d32f2f', 0.1),
              transform: 'scale(1.1)',
            },
          }}
        >
          <DeleteIcon fontSize="small" color="error" />
        </IconButton>
      </Tooltip>
    </Card>
  );
}

/**
 * Category item card for the draft list
 */
interface CategoryItemCardDraftProps {
  /** Category data */
  category: CategoryDraft;
  /** 1-based display position */
  position: number;
  /** Whether currently being dragged */
  isDragging: boolean;
  /** Drag handle props */
  dragHandleProps: React.HTMLAttributes<HTMLDivElement> | null | undefined;
  /** Toggle visibility callback - receives category ID and field */
  onToggleCategoryVisibility: (categoryId: string, field: keyof CategoryDraft) => void;
  /** View products callback - receives category ID and name */
  onViewProducts: (categoryId: string, categoryName: string) => void;
  /** Toggle position lock callback - receives category ID and position */
  onToggleLockPosition: (categoryId: string, position: number) => void;
  /** Product count for this category at selected store (undefined = loading) */
  productCount?: number;
  /** Hide stock info (product count, inventory eye) - used in All Locations mode */
  hideStockInfo?: boolean;
  /** Lock position (may differ from display position, e.g., Home tab has +1 offset for New & Popular) */
  lockPosition?: number;
}

const CategoryItemCardDraft = React.memo(function CategoryItemCardDraft({
  category,
  position,
  isDragging,
  dragHandleProps,
  onToggleCategoryVisibility,
  onViewProducts,
  onToggleLockPosition,
  productCount,
  hideStockInfo = false,
  lockPosition,
}: CategoryItemCardDraftProps): React.ReactElement {
  // In All Locations mode, don't show products as "no products"
  const hasNoProducts = !hideStockInfo && productCount === 0;
  const isLocked = category.lockedGlobalPosition !== null && category.lockedGlobalPosition !== undefined;
  // Use lockPosition if provided, otherwise fall back to position
  const effectiveLockPosition = lockPosition ?? position;

  // Memoize callbacks inside the component to avoid re-renders
  const handleToggleVisibility = useCallback((field: keyof CategoryDraft) => {
    onToggleCategoryVisibility(category.id, field);
  }, [onToggleCategoryVisibility, category.id]);

  const handleViewProducts = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onViewProducts(category.id, category.name);
  }, [onViewProducts, category.id, category.name]);

  const handleToggleLock = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleLockPosition(category.id, effectiveLockPosition);
  }, [onToggleLockPosition, category.id, effectiveLockPosition]);

  return (
    <Card
      variant="outlined"
      sx={{
        opacity: category.isActive ? (hasNoProducts ? 0.5 : 1) : 0.4,
        // Only apply transitions when not dragging for better performance
        transition: isDragging ? 'none' : 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        // Not clickable any more: the card opened the category editor, and
        // editing now lives only in the All Categories tab.
        cursor: 'default',
        border: '1px solid',
        borderColor: isDragging ? 'primary.main' : hasNoProducts ? 'warning.light' : 'divider',
        background: isDragging
          ? alpha('#1976d2', 0.04)
          : hasNoProducts
            ? alpha('#ff9800', 0.04)
            : 'background.paper',
        '&:hover': isDragging ? {} : {
          borderColor: hasNoProducts ? 'warning.main' : 'primary.light',
          boxShadow: hasNoProducts
            ? '0 4px 12px rgba(255, 152, 0, 0.12)'
            : '0 4px 12px rgba(99, 102, 241, 0.12)',
          transform: 'translateY(-2px)',
        },
        '&:active': isDragging ? {} : {
          transform: 'translateY(0)',
        },
      }}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Drag handle */}
          <Box
            {...dragHandleProps}
            sx={{
              cursor: isDragging ? 'grabbing' : 'grab',
              display: 'flex',
              alignItems: 'center',
              color: isDragging ? 'primary.main' : 'action.active',
              transition: 'color 0.2s ease',
            }}
          >
            <DragIndicatorIcon />
          </Box>

          {/* Position number */}
          <Typography
            variant="caption"
            sx={{
              width: 24,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200',
              color: (theme) => theme.palette.mode === 'dark' ? 'grey.100' : 'grey.800',
              fontWeight: 600,
            }}
          >
            {position}
          </Typography>

          {/* Category image */}
          <Box
            sx={{
              width: 48,
              height: 48,
              backgroundColor: '#FF002C',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {category.imageUrl ? (
              <img
                src={category.imageUrl}
                alt={category.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <CategoryIcon sx={{ fontSize: 24, color: 'rgba(255,255,255,0.7)' }} />
            )}
          </Box>

          {/* Category info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight={500} noWrap>
                {category.name}
              </Typography>
              {/* Product count badge - hidden in All Locations mode */}
              {!hideStockInfo && productCount !== undefined && (
                <Chip
                  label={productCount}
                  size="small"
                  variant="filled"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    minWidth: 28,
                    backgroundColor: productCount > 0 ? '#2E7D32' : '#ED6C02',
                    color: '#FFFFFF',
                    '& .MuiChip-label': { color: '#FFFFFF' },
                  }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {getFilterTypeLabel(category.homeFilterType)}
              {category.homeFilterType === 'tag' && category.homeFilterValue && `: ${category.homeFilterValue}`}
              {!hideStockInfo && hasNoProducts && ' • No products at this store'}
            </Typography>
          </Box>

          {/* Lock position button */}
          <Tooltip title={isLocked ? `Locked at position ${category.lockedGlobalPosition} (click to unlock)` : `Lock position ${effectiveLockPosition} across all screens`}>
            <IconButton
              size="small"
              onClick={handleToggleLock}
              sx={{
                transition: 'all 0.2s ease',
                color: isLocked ? 'warning.main' : 'action.disabled',
                '&:hover': {
                  backgroundColor: isLocked ? alpha('#ff9800', 0.1) : alpha('#9e9e9e', 0.1),
                  transform: 'scale(1.1)',
                },
              }}
            >
              {isLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          {/* View products button - hidden in All Locations mode */}
          {!hideStockInfo && (
            <Tooltip title="View products in this category">
              <IconButton
                size="small"
                onClick={handleViewProducts}
                sx={{
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: alpha('#4caf50', 0.1),
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* Edit button */}
        </Box>
      </CardContent>
    </Card>
  );
});

// =============================================================================
// New & Popular Config Dialog
// =============================================================================

const FILTER_TYPE_OPTIONS: { value: HomeFilterType; label: string }[] = [
  { value: 'newOrPopular', label: 'New & Popular (auto from Vend)' },
  { value: 'tag', label: 'By Tag' },
  { value: 'category', label: 'By Category' },
  { value: 'brand', label: 'By Brand' },
  { value: 'combined', label: 'Combined (tags + categories)' },
];

function NewPopularConfigDialog({
  open,
  onClose,
  config,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  config: NewPopularConfig;
  onSave: (config: NewPopularConfig) => void;
}): React.ReactElement {
  const [name, setName] = useState(config.name);
  const [filterType, setFilterType] = useState<HomeFilterType>(config.homeFilterType);
  const [filterValue, setFilterValue] = useState(config.homeFilterValue || '');

  // Reset form when config changes (dialog opens with fresh data)
  useEffect(() => {
    if (open) {
      setName(config.name);
      setFilterType(config.homeFilterType);
      setFilterValue(config.homeFilterValue || '');
    }
  }, [open, config]);

  const needsFilterValue = filterType === 'tag' || filterType === 'brand' || filterType === 'combined';

  const combinedJsonError = (() => {
    if (filterType !== 'combined' || !filterValue.trim()) return '';
    try {
      const parsed = JSON.parse(filterValue.trim());
      if (!parsed.categories && !parsed.tags) return 'Must have "categories" and/or "tags" arrays';
      return '';
    } catch {
      return 'Invalid JSON';
    }
  })();

  const handleSave = () => {
    onSave({
      name: name.trim() || 'New & Popular',
      homeFilterType: filterType,
      homeFilterValue: needsFilterValue ? (filterValue.trim() || null) : null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TrendingUpIcon color="warning" />
        Configure New & Popular Block
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          This block always appears first on the home screen. Configure which products appear in it.
        </DialogContentText>

        <TextField
          label="Display Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          placeholder="New & Popular"
        />

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Filter Type</InputLabel>
          <Select
            value={filterType}
            label="Filter Type"
            onChange={(e) => {
              setFilterType(e.target.value as HomeFilterType);
              setFilterValue('');
            }}
          >
            {FILTER_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {needsFilterValue && (
          <TextField
            label={
              filterType === 'tag' ? 'Tag Name' :
              filterType === 'brand' ? 'Brand Name' :
              'Combined Filter (JSON: {"categories":[],"tags":[]})'
            }
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            fullWidth
            size="small"
            placeholder={
              filterType === 'tag' ? 'e.g. summer-sale' :
              filterType === 'brand' ? 'e.g. Coca-Cola' :
              '{"categories":["Drinks"],"tags":["sale"]}'
            }
            error={!!combinedJsonError}
            helperText={
              combinedJsonError ||
              (filterType === 'combined'
                ? 'JSON with "categories" and/or "tags" arrays'
                : undefined)
            }
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="warning"
          disabled={(needsFilterValue && !filterValue.trim()) || !!combinedJsonError}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Configuration Panel Component
 *
 * Main configuration panel containing banner sections and category list.
 * All changes are made to the draft and not immediately saved to the database.
 */
// Tab definitions for the configuration panel
export type ConfigTab = 'home' | 'carousel' | 'shop' | 'brands' | 'all' | 'banners';

export interface ConfigurationPanelProps {
  /** Controlled section. Omit to let the panel own its own tab state. */
  activeTab?: ConfigTab;
  /** Fires on every section change, controlled or not. */
  onTabChange?: (tab: ConfigTab) => void;
  /**
   * Hide the built-in "Preview As" store picker. The page moves it next to the
   * preview it actually affects, so it isn't mistaken for a layout switch.
   */
  hideStoreSelector?: boolean;
  /**
   * Hide the tab strip. Set when the page has already navigated into one
   * section from the hub — the strip is then a second copy of navigation you've
   * just used, and it makes the section look like it never really opened.
   * Also hides the shared controls a section has no use for, so what's left is
   * that section and nothing else.
   */
  hideTabs?: boolean;
}

export default React.memo(function ConfigurationPanel({
  activeTab: controlledTab,
  onTabChange,
  hideStoreSelector = false,
  hideTabs = false,
}: ConfigurationPanelProps = {}): React.ReactElement {
  const {
    draft,
    liveState,
    isLoading,
    isSaving,
    updateBannerConfig,
    updateCategoryConfig,
    updateCarouselVerticalConfig,
    updateBrandPositions,
    updatePresence,
    refreshDraft,
    trackDragOperation,
    selectedStoreId,
    setSelectedStoreId,
  } = useHomeLayout();

  // Tab state. Controlled when the page drives it from the section hub,
  // self-managed otherwise so the panel still works standalone.
  const [uncontrolledTab, setUncontrolledTab] = useState<ConfigTab>('home');
  const activeTab = controlledTab ?? uncontrolledTab;

  /**
   * Sections whose content is the category list. Banners and Brands have their
   * own data, so the category-wide controls (locking, empty-category grouping)
   * are noise there.
   */
  const isCategorySection = activeTab !== 'banners' && activeTab !== 'brands';
  const setActiveTab = useCallback(
    (tab: ConfigTab) => {
      setUncontrolledTab(tab);
      onTabChange?.(tab);
    },
    [onTabChange]
  );

  // Store state for config panel
  const { isStoreVisible } = useStoreVisibility();
  const [allStores, setAllStores] = useState<Store[]>([]);
  const stores = useMemo(() => allStores.filter(isStoreVisible), [allStores, isStoreVisible]);
  const [storesLoading, setStoresLoading] = useState(true);

  // Product counts per category for selected store
  const [categoryProductCounts, setCategoryProductCounts] = useState<Record<string, number>>({});
  const [countsLoading, setCountsLoading] = useState(false);

  const [emptyCollapsed, setEmptyCollapsed] = useState(false);
  const [availableCollapsed, setAvailableCollapsed] = useState(false);

  // Category search/filter
  const [categorySearchQuery, setCategorySearchQuery] = useState('');

  // Fetch stores on mount.
  //
  // No auto-select: the scope defaults to ALL_LOCATIONS (see HomeLayoutContext).
  // This used to pin the screen to `data.find(s => s.name === 'Deeliver Siep River')`
  // — a store that does not exist under that spelling ("Siep" vs "Diep"), so the
  // find always missed and it silently fell through to `data[0]`, scoping every
  // admin to whichever store happened to sort first.
  useEffect(() => {
    setStoresLoading(true);
    fetchStores().then((data) => {
      setAllStores(data);
      setStoresLoading(false);
    });
  }, []);

  // When "All Locations" selected, skip fetching (treat all as available)
  const isAllLocations = selectedStoreId === ALL_LOCATIONS;

  // Stable fingerprint of categories that only changes when category set or filter
  // settings change - NOT when positions change (which happens on every drag).
  // This prevents product count re-fetches on every drag reorder.
  const categoryFingerprint = useMemo(() => {
    const categories = draft?.categoryConfig?.categories || [];
    return categories
      .map((c) => `${c.id}:${c.homeFilterType || ''}:${c.homeFilterValue || ''}`)
      .sort()
      .join('|');
  }, [draft?.categoryConfig?.categories]);

  // Fetch product counts for ALL categories and brands in a single API call.
  // Uses GET /admin/home-layout/product-counts (materialized view + 5-min backend cache).
  // Replaces the old pattern of 30-70 individual /products?limit=1 calls that caused rate limiting.
  useEffect(() => {
    let cancelled = false;
    const categories = draft?.categoryConfig?.categories || [];
    if (categories.length === 0) {
      setCategoryProductCounts({});
      return;
    }

    // "All Locations" mode: skip product count fetching, all categories are shown
    if (isAllLocations) {
      const allAvailable: Record<string, number> = {};
      categories.forEach((cat) => { allAvailable[cat.id] = -1; });
      setCategoryProductCounts(allAvailable);
      setCountsLoading(false);
      return;
    }

    if (!selectedStoreId) return;

    setCountsLoading(true);
    setBrandCountsLoading(true);

    fetchAllProductCounts(selectedStoreId).then((result) => {
      if (cancelled) return;
      setCategoryProductCounts(result.categories || {});
      setCountsLoading(false);
      setBrandProductCounts(result.brands || {});
      setBrandCountsLoading(false);
    });

    return () => { cancelled = true; };
  }, [selectedStoreId, categoryFingerprint, isAllLocations]);

  // Get selected store info
  const selectedStore = stores.find((s) => s.id === selectedStoreId);


  // Banner section expansion state
  const [expandedBanners, setExpandedBanners] = useState<Record<string, boolean>>({
    'top banner': false,
    'slider banner': false,
    'bottom banner': false,
  });

  // Category section expansion (always expanded by default)
  const [categoriesExpanded, setCategoriesExpanded] = useState(true);
  // Track when any item is being dragged (for expandable drop zones)
  const [isDraggingActive, setIsDraggingActive] = useState(false);

  // Home tab categories: only showOnHomePage=true (for product scrollers).
  // One layout for every store (owner, 1 Nov 2026): the list is the show tick, not
  // what a store stocks. Active categories without the tick sit in "available to add".
  const { homeCategories, hiddenHomeCategories, availableHomeCategories, inactiveCategories } = useMemo(() => {
    const categories = draft?.categoryConfig?.categories || [];

    const sortedActiveHome = categories
      .filter((cat) => cat.isActive && cat.showOnHomePage)
      .sort((a, b) => (a.scrollerPosition ?? a.position ?? 999) - (b.scrollerPosition ?? b.position ?? 999));

    const available = categories
      .filter((cat) => cat.isActive && !cat.showOnHomePage)
      .sort((a, b) => (a.position ?? 999) - (b.position ?? 999));

    return {
      homeCategories: sortedActiveHome,
      hiddenHomeCategories: [] as CategoryDraft[],
      availableHomeCategories: available,
      inactiveCategories: categories.filter((cat) => !cat.isActive),
    };
  }, [draft?.categoryConfig?.categories]);

  // Combined home list: interleaves categories with carousel and brand scroller placeholders
  // Includes "New & Popular" as a non-draggable first item to represent the full mobile layout
  type HomeItem = { type: 'category' | 'carousel' | 'brand-scroller' | 'new-popular'; id: string; sortKey: number; category?: CategoryDraft };
  const homeListItems = useMemo((): HomeItem[] => {
    const items: HomeItem[] = [];

    // "New & Popular" is always first on mobile — show it as a locked item
    items.push({ type: 'new-popular', id: 'new-popular', sortKey: 0 });

    homeCategories.forEach((cat, i) => {
      items.push({ type: 'category', id: cat.id, sortKey: (i + 1) * 2, category: cat });
    });

    // Carousel position
    const cConfig = draft?.carouselVerticalConfig;
    const carouselPos = cConfig?.mode === 'custom' && cConfig.customPosition != null
      ? cConfig.customPosition
      : Math.ceil(homeCategories.length / 2); // auto = 50%
    items.push({ type: 'carousel', id: 'carousel-placeholder', sortKey: carouselPos * 2 + 1 });

    // Brand scroller position
    const bConfig = draft?.brandPositions?.scrollerConfig;
    const brandScrollerPos = bConfig?.mode === 'custom' && bConfig.customPosition != null
      ? bConfig.customPosition
      : homeCategories.length + 1; // auto = bottom
    items.push({ type: 'brand-scroller', id: 'brand-scroller-placeholder', sortKey: brandScrollerPos * 2 + 1 });

    return items.sort((a, b) => a.sortKey - b.sortKey);
  }, [homeCategories, draft?.carouselVerticalConfig, draft?.brandPositions?.scrollerConfig]);

  // Brands tab state
  const [allBrands, setAllBrands] = useState<Brand[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [brandSearch, setBrandSearch] = useState('');
  const [brandProductCounts, setBrandProductCounts] = useState<Record<string, number>>({});
  const [brandCountsLoading, setBrandCountsLoading] = useState(false);
  const [brandProductsModal, setBrandProductsModal] = useState<{ brandName: string } | null>(null);
  // Add/edit dialog: `{ brand: null }` means "adding", a brand means "editing".
  const [brandForm, setBrandForm] = useState<{ brand: Brand | null } | null>(null);
  const [brandPendingDelete, setBrandPendingDelete] = useState<Brand | null>(null);
  const [brandDeleting, setBrandDeleting] = useState(false);

  /**
   * Load the rail from the API, ordered by draft positions where we have them.
   *
   * Extracted from the mount effect so add/edit/delete can pull a fresh list
   * afterwards — the effect only fires while `allBrands` is empty.
   */
  const loadBrands = useCallback(async (): Promise<void> => {
    setBrandsLoading(true);
    try {
      const { json } = await httpClient(`${config.API_URL}/brands?limit=100`, { method: 'GET' });
      const brands = Array.isArray(json) ? json : ((json as { data: Brand[] }).data || []);
      // Sort by draft positions if available, otherwise use live positions
      const draftPositions = draft?.brandPositions?.positions;
      if (draftPositions && draftPositions.length > 0) {
        const posMap = new Map(draftPositions.map((bp: { id: string; position: number }) => [bp.id, bp.position]));
        brands.sort((a: Brand, b: Brand) => (posMap.get(a.id) ?? a.position ?? 999) - (posMap.get(b.id) ?? b.position ?? 999));
      }
      setAllBrands(brands);
    } catch (err) {
      Logger.error('Failed to fetch brands:', err);
    } finally {
      setBrandsLoading(false);
    }
  }, [draft?.brandPositions]);

  // Fetch all brands when Brands tab is active, sorted by draft positions if available
  useEffect(() => {
    if (activeTab === 'brands' && allBrands.length === 0 && !brandsLoading) {
      void loadBrands();
    }
  }, [activeTab, allBrands.length, brandsLoading, loadBrands]);

  /** After a create/edit: the API cache is already cleared, so just re-read. */
  const handleBrandSaved = useCallback((): void => {
    clearBrandsCache();
    void loadBrands();
  }, [loadBrands]);

  const handleBrandDelete = useCallback(async (): Promise<void> => {
    if (!brandPendingDelete) return;
    setBrandDeleting(true);
    try {
      await deleteBrand(brandPendingDelete.id);
      setBrandPendingDelete(null);
      await loadBrands();
    } catch (err) {
      Logger.error('Failed to delete brand:', err);
    } finally {
      setBrandDeleting(false);
    }
  }, [brandPendingDelete, loadBrands]);

  // Brand counts for "All Locations" mode only.
  // Normal store-specific counts are fetched by the combined fetchAllProductCounts call above.
  useEffect(() => {
    if (allBrands.length === 0) {
      setBrandProductCounts({});
      return;
    }

    if (isAllLocations) {
      const allAvailable: Record<string, number> = {};
      allBrands.forEach((b) => { allAvailable[b.id] = -1; });
      setBrandProductCounts(allAvailable);
      setBrandCountsLoading(false);
    }
  }, [allBrands, isAllLocations]);

  // Filter brands by search (for display only — positions are on the full list)
  const filteredBrands = useMemo(() => {
    if (!brandSearch.trim()) return allBrands;
    const search = brandSearch.toLowerCase();
    return allBrands.filter(
      (brand) =>
        brand.name.toLowerCase().includes(search) ||
        // Brands added from this screen have no SKU — that column is a leftover
        // from the DEAR "Brandxx - 001" families.
        (brand.sku || '').toLowerCase().includes(search)
    );
  }, [allBrands, brandSearch]);

  // Handle brand drag-and-drop reorder (saves to draft, not directly to live DB)
  const handleBrandDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) return;

    const { source, destination } = result;
    if (source.index === destination.index) return;

    const movedBrand = allBrands[source.index];
    const reordered = Array.from(allBrands);
    const [removed] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, removed);

    // Update local state immediately for smooth UX
    setAllBrands(reordered);

    // Track the operation for meaningful change counting
    const livePositions = liveState?.brandPositions?.positions;
    const livePos = livePositions?.find((bp) => bp.id === movedBrand.id)?.position ?? (source.index + 1);
    trackDragOperation({
      itemId: movedBrand.id,
      itemName: movedBrand.name,
      section: 'brands',
      positionField: 'position',
      fromPosition: livePos,
      toPosition: destination.index + 1,
      lastMovedAt: new Date().toISOString(),
    });

    // Persist to draft (published together with other changes)
    updateBrandPositions({
      positions: reordered.map((b, i) => ({
        id: b.id,
        position: i + 1,
        name: b.name,
      })),
    });
  }, [allBrands, updateBrandPositions, liveState?.brandPositions?.positions, trackDragOperation]);

  // Filtered categories for Carousel tab (same order for mobile and web): ticked for
  // the mobile OR the web carousel. Active categories without the tick sit in "available to add".
  const { carouselCategories, hiddenCarouselCategories, availableCarouselCategories } = useMemo(() => {
    const categories = draft?.categoryConfig?.categories || [];

    const sortedActiveCarousel = categories
      .filter((cat) => cat.isActive && (cat.showInCarousel || cat.showInCarouselWeb))
      .sort((a, b) => (a.carouselPosition ?? a.position) - (b.carouselPosition ?? b.position));

    const available = categories
      .filter((cat) => cat.isActive && !cat.showInCarousel && !cat.showInCarouselWeb)
      .sort((a, b) => (a.position ?? 999) - (b.position ?? 999));

    return { carouselCategories: sortedActiveCarousel, hiddenCarouselCategories: [] as CategoryDraft[], availableCarouselCategories: available };
  }, [draft?.categoryConfig?.categories]);

  // Filtered categories for Shop tab: only showInShop=true. Active categories without
  // the tick sit in "available to add".
  const { shopCategories, hiddenShopCategories, availableShopCategories } = useMemo(() => {
    const categories = draft?.categoryConfig?.categories || [];

    const sortedActiveShop = categories
      .filter((cat) => cat.isActive && cat.showInShop)
      .sort((a, b) => (a.shopPosition ?? a.position) - (b.shopPosition ?? b.position));

    const available = categories
      .filter((cat) => cat.isActive && !cat.showInShop)
      .sort((a, b) => (a.position ?? 999) - (b.position ?? 999));

    return { shopCategories: sortedActiveShop, hiddenShopCategories: [] as CategoryDraft[], availableShopCategories: available };
  }, [draft?.categoryConfig?.categories]);

  // Filtered arrays when search is active — keep non-category items, filter categories by name
  const isSearchActive = categorySearchQuery.trim().length > 0;
  const searchLower = categorySearchQuery.toLowerCase();

  const filteredHomeListItems = useMemo(() => {
    if (!isSearchActive) return homeListItems;
    return homeListItems.filter((item) =>
      item.type !== 'category' || item.category!.name.toLowerCase().includes(searchLower)
    );
  }, [homeListItems, isSearchActive, searchLower]);

  const filteredCarouselCategories = useMemo(() => {
    if (!isSearchActive) return carouselCategories;
    return carouselCategories.filter((c) => c.name.toLowerCase().includes(searchLower));
  }, [carouselCategories, isSearchActive, searchLower]);

  const filteredShopCategories = useMemo(() => {
    if (!isSearchActive) return shopCategories;
    return shopCategories.filter((c) => c.name.toLowerCase().includes(searchLower));
  }, [shopCategories, isSearchActive, searchLower]);

  const filteredHiddenHomeCategories = useMemo(() => {
    if (!isSearchActive) return hiddenHomeCategories;
    return hiddenHomeCategories.filter((c) => c.name.toLowerCase().includes(searchLower));
  }, [hiddenHomeCategories, isSearchActive, searchLower]);

  const filteredHiddenCarouselCategories = useMemo(() => {
    if (!isSearchActive) return hiddenCarouselCategories;
    return hiddenCarouselCategories.filter((c) => c.name.toLowerCase().includes(searchLower));
  }, [hiddenCarouselCategories, isSearchActive, searchLower]);

  const filteredHiddenShopCategories = useMemo(() => {
    if (!isSearchActive) return hiddenShopCategories;
    return hiddenShopCategories.filter((c) => c.name.toLowerCase().includes(searchLower));
  }, [hiddenShopCategories, isSearchActive, searchLower]);

  // Tab counts: the same rule as the lists above (active + the tab's show tick)
  const tabCounts = useMemo(() => {
    const categories = draft?.categoryConfig?.categories || [];
    const bannerPositions = draft?.bannerConfig?.positions || [];

    const homeCount = homeCategories.length;
    const carouselCount = carouselCategories.length;
    const shopCount = shopCategories.length;

    const brandsCount = draft?.brandPositions?.positions?.length ?? allBrands.length;

    const allCount = categories.length;

    const bannersCount = bannerPositions.reduce(
      (sum, pos) => sum + pos.items.filter((i) => i.isActive).length, 0
    );

    return { homeCount, carouselCount, shopCount, brandsCount, allCount, bannersCount };
  }, [draft, homeCategories, carouselCategories, shopCategories, allBrands.length]);

  // Category dialog state
  // New & Popular config dialog state
  const [newPopularDialogOpen, setNewPopularDialogOpen] = useState(false);

  // Banner dialog state
  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [editingBannerItem, setEditingBannerItem] = useState<BannerItemDraft | null>(null);
  const [currentBannerPosition, setCurrentBannerPosition] = useState<BannerPositionDraft | null>(null);

  // Banner delete confirmation state
  const [deleteBannerConfirmOpen, setDeleteBannerConfirmOpen] = useState(false);
  const [deleteBannerTarget, setDeleteBannerTarget] = useState<{ positionName: string; item: BannerItemDraft } | null>(null);

  // Products modal state (for viewing products in a category)
  const [productsModalOpen, setProductsModalOpen] = useState(false);
  const [productsModalCategory, setProductsModalCategory] = useState<{ id: string; name: string } | null>(null);

  // Handler to open products modal
  const handleViewCategoryProducts = useCallback((categoryId: string, categoryName: string) => {
    setProductsModalCategory({ id: categoryId, name: categoryName });
    setProductsModalOpen(true);
  }, []);

  // Handler to close products modal
  const handleCloseProductsModal = useCallback(() => {
    setProductsModalOpen(false);
    setProductsModalCategory(null);
  }, []);

  // Toggle banner section expansion
  const toggleBannerSection = useCallback((name: string) => {
    setExpandedBanners((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
    updatePresence('banners');
  }, [updatePresence]);

  // Handle drag start - track when dragging begins
  const handleDragStart = useCallback(() => {
    setIsDraggingActive(true);
  }, []);

  // Handle category drag end - updates positions based on active tab
  const handleCategoryDragEnd = useCallback(
    (result: DropResult) => {
      setIsDraggingActive(false);

      if (!result.destination) return;

      const { source, destination, draggableId } = result;
      if (source.index === destination.index) return;

      const allCategories = draft?.categoryConfig?.categories || [];
      const liveCategories = liveState?.categoryConfig?.categories || [];

      // Helper to get live position for a category
      const getLivePosition = (catId: string, field: 'scrollerPosition' | 'carouselPosition' | 'shopPosition') => {
        const liveCat = liveCategories.find((c) => c.id === catId);
        return liveCat?.[field] ?? (source.index + 1);
      };

      // Helper to reassign positions for a tab when moving a locked category globally
      const reassignPositions = (
        cats: CategoryDraft[],
        targetId: string,
        lockedPos: number,
        positionField: 'scrollerPosition' | 'carouselPosition' | 'shopPosition',
        visibilityField: 'showOnHomePage' | 'showInCarousel' | 'showInShop'
      ): Map<string, number> => {
        const positionMap = new Map<string, number>();

        // Get visible categories excluding target, sorted by current position
        const visibleOthers = cats
          .filter((c) => c.id !== targetId && c[visibilityField] && c.isActive)
          .sort((a, b) => a[positionField] - b[positionField]);

        // Target gets the locked position
        positionMap.set(targetId, lockedPos);

        // Assign positions to others, skipping the locked position
        let pos = 1;
        for (const cat of visibleOthers) {
          if (pos === lockedPos) pos++; // Skip locked position
          positionMap.set(cat.id, pos);
          pos++;
        }

        return positionMap;
      };

      // Determine which position field and which source list to use based on active tab
      if (activeTab === 'home') {
        // Map destination.index to category-relative position by counting only category items before destination
        const getCategoryPosition = (destIndex: number): number => {
          let categoryCount = 0;
          for (let i = 0; i < destIndex; i++) {
            if (homeListItems[i]?.type === 'category') categoryCount++;
          }
          return categoryCount + 1;
        };

        // Check if this is the carousel placeholder being dragged
        if (draggableId === 'carousel-placeholder') {
          const newPosition = getCategoryPosition(destination.index);
          updateCarouselVerticalConfig({
            mode: 'custom',
            customPosition: newPosition,
          });

          trackDragOperation({
            itemId: 'carousel-placeholder',
            itemName: 'Carousel Position',
            section: 'home',
            positionField: 'carouselVerticalConfig.customPosition',
            fromPosition: draft?.carouselVerticalConfig?.customPosition || Math.ceil(homeCategories.length / 2),
            toPosition: newPosition,
            lastMovedAt: new Date().toISOString(),
          });
          return;
        }

        // Check if this is the brand scroller placeholder being dragged
        if (draggableId === 'brand-scroller-placeholder') {
          const newPosition = getCategoryPosition(destination.index);
          updateBrandPositions({
            ...(draft?.brandPositions || { positions: [] }),
            scrollerConfig: { mode: 'custom', customPosition: newPosition },
          });

          trackDragOperation({
            itemId: 'brand-scroller-placeholder',
            itemName: 'Brand Scroller Position',
            section: 'home',
            positionField: 'brandPositions.scrollerConfig.customPosition',
            fromPosition: draft?.brandPositions?.scrollerConfig?.customPosition || homeCategories.length + 1,
            toPosition: newPosition,
            lastMovedAt: new Date().toISOString(),
          });
          return;
        }

        // Home tab: reorder homeCategories, update scrollerPosition (and legacy position)
        // Map source/destination indices from homeListItems to homeCategories indices
        const sourceItem = homeListItems[source.index];
        const sourceCatIndex = homeCategories.findIndex(c => c.id === sourceItem?.id);
        if (sourceCatIndex === -1) return;
        const movedCat = homeCategories[sourceCatIndex];
        // Map destination index to category-only position
        const destCategoryIndex = getCategoryPosition(destination.index) - 1;
        const targetPosition = destCategoryIndex + 1;
        const isLockedCategory = movedCat.lockedGlobalPosition !== null && movedCat.lockedGlobalPosition !== undefined;

        // If dragging a locked category, check for conflicts and update ALL tabs
        if (isLockedCategory) {
          // Check if another locked category is at the target position
          const conflictingCategory = allCategories.find(
            (c) => c.id !== movedCat.id && c.lockedGlobalPosition === targetPosition
          );

          if (conflictingCategory) {
            // Show conflict dialog
            setDragLockConflictDialog({
              open: true,
              categoryId: movedCat.id,
              categoryName: movedCat.name,
              targetPosition,
              conflictingCategoryName: conflictingCategory.name,
              conflictingCategoryId: conflictingCategory.id,
            });
            return; // Don't complete the drag
          }

          // No conflict - move the locked category globally on ALL tabs
          const homePositions = reassignPositions(allCategories, movedCat.id, targetPosition, 'scrollerPosition', 'showOnHomePage');
          const carouselPositions = reassignPositions(allCategories, movedCat.id, targetPosition, 'carouselPosition', 'showInCarousel');
          const shopPositions = reassignPositions(allCategories, movedCat.id, targetPosition, 'shopPosition', 'showInShop');

          // Track the operation
          trackDragOperation({
            itemId: movedCat.id,
            itemName: movedCat.name,
            section: 'home',
            positionField: 'scrollerPosition',
            fromPosition: getLivePosition(movedCat.id, 'scrollerPosition'),
            toPosition: targetPosition,
            lastMovedAt: new Date().toISOString(),
          });

          // Apply all position changes
          const updatedCategories = allCategories.map((cat) => {
            const updates: Partial<CategoryDraft> = {};

            // Update the locked position
            if (cat.id === movedCat.id) {
              updates.lockedGlobalPosition = targetPosition;
            }

            if (homePositions.has(cat.id)) {
              const pos = homePositions.get(cat.id)!;
              updates.position = pos;
              updates.scrollerPosition = pos;
            }
            if (carouselPositions.has(cat.id)) {
              updates.carouselPosition = carouselPositions.get(cat.id)!;
            }
            if (shopPositions.has(cat.id)) {
              updates.shopPosition = shopPositions.get(cat.id)!;
            }

            return Object.keys(updates).length > 0 ? { ...cat, ...updates } : cat;
          });

          updateCategoryConfig({ categories: updatedCategories });
          return;
        }

        // Non-locked category: normal single-tab reorder
        const reorderedActive = Array.from(homeCategories);
        const [removed] = reorderedActive.splice(sourceCatIndex, 1);
        reorderedActive.splice(destCategoryIndex, 0, removed);

        // Track the operation
        trackDragOperation({
          itemId: movedCat.id,
          itemName: movedCat.name,
          section: 'home',
          positionField: 'scrollerPosition',
          fromPosition: getLivePosition(movedCat.id, 'scrollerPosition'),
          toPosition: targetPosition,
          lastMovedAt: new Date().toISOString(),
        });

        // Create map of new positions for active categories
        const newPositions = new Map<string, number>();
        reorderedActive.forEach((cat, index) => {
          newPositions.set(cat.id, index + 1);
        });

        // Update all categories, preserving inactive ones at the end
        const updatedCategories = allCategories.map((cat) => {
          if (newPositions.has(cat.id)) {
            const pos = newPositions.get(cat.id)!;
            return { ...cat, position: pos, scrollerPosition: pos };
          }
          return cat;
        });

        updateCategoryConfig({ categories: updatedCategories });
      } else if (activeTab === 'carousel') {
        // Carousel tab: reorder carouselCategories, update carouselPosition
        const movedCat = carouselCategories[source.index];
        const targetPosition = destination.index + 1;
        const isLockedCategory = movedCat.lockedGlobalPosition !== null && movedCat.lockedGlobalPosition !== undefined;

        // If dragging a locked category, check for conflicts and update ALL tabs
        if (isLockedCategory) {
          // Check if another locked category is at the target position
          const conflictingCategory = allCategories.find(
            (c) => c.id !== movedCat.id && c.lockedGlobalPosition === targetPosition
          );

          if (conflictingCategory) {
            // Show conflict dialog
            setDragLockConflictDialog({
              open: true,
              categoryId: movedCat.id,
              categoryName: movedCat.name,
              targetPosition,
              conflictingCategoryName: conflictingCategory.name,
              conflictingCategoryId: conflictingCategory.id,
            });
            return; // Don't complete the drag
          }

          // No conflict - move the locked category globally on ALL tabs
          const homePositions = reassignPositions(allCategories, movedCat.id, targetPosition, 'scrollerPosition', 'showOnHomePage');
          const carouselPositions = reassignPositions(allCategories, movedCat.id, targetPosition, 'carouselPosition', 'showInCarousel');
          const shopPositions = reassignPositions(allCategories, movedCat.id, targetPosition, 'shopPosition', 'showInShop');

          // Track the operation
          trackDragOperation({
            itemId: movedCat.id,
            itemName: movedCat.name,
            section: 'carousel',
            positionField: 'carouselPosition',
            fromPosition: getLivePosition(movedCat.id, 'carouselPosition'),
            toPosition: targetPosition,
            lastMovedAt: new Date().toISOString(),
          });

          // Apply all position changes
          const updatedCategories = allCategories.map((cat) => {
            const updates: Partial<CategoryDraft> = {};

            // Update the locked position
            if (cat.id === movedCat.id) {
              updates.lockedGlobalPosition = targetPosition;
            }

            if (homePositions.has(cat.id)) {
              const pos = homePositions.get(cat.id)!;
              updates.position = pos;
              updates.scrollerPosition = pos;
            }
            if (carouselPositions.has(cat.id)) {
              updates.carouselPosition = carouselPositions.get(cat.id)!;
            }
            if (shopPositions.has(cat.id)) {
              updates.shopPosition = shopPositions.get(cat.id)!;
            }

            return Object.keys(updates).length > 0 ? { ...cat, ...updates } : cat;
          });

          updateCategoryConfig({ categories: updatedCategories });
          return;
        }

        // Non-locked category: normal single-tab reorder
        const reorderedCarousel = Array.from(carouselCategories);
        const [removed] = reorderedCarousel.splice(source.index, 1);
        reorderedCarousel.splice(destination.index, 0, removed);

        // Track the operation
        trackDragOperation({
          itemId: movedCat.id,
          itemName: movedCat.name,
          section: 'carousel',
          positionField: 'carouselPosition',
          fromPosition: getLivePosition(movedCat.id, 'carouselPosition'),
          toPosition: destination.index + 1,
          lastMovedAt: new Date().toISOString(),
        });

        // Create map of new carousel positions
        const newCarouselPositions = new Map<string, number>();
        reorderedCarousel.forEach((cat, index) => {
          newCarouselPositions.set(cat.id, index + 1);
        });

        // Update only carouselPosition for affected categories
        const updatedCategories = allCategories.map((cat) => {
          if (newCarouselPositions.has(cat.id)) {
            return { ...cat, carouselPosition: newCarouselPositions.get(cat.id)! };
          }
          return cat;
        });

        updateCategoryConfig({ categories: updatedCategories });
      } else if (activeTab === 'shop') {
        // Shop tab: reorder shopCategories, update shopPosition
        const movedCat = shopCategories[source.index];
        const targetPosition = destination.index + 1;
        const isLockedCategory = movedCat.lockedGlobalPosition !== null && movedCat.lockedGlobalPosition !== undefined;

        // If dragging a locked category, check for conflicts and update ALL tabs
        if (isLockedCategory) {
          // Check if another locked category is at the target position
          const conflictingCategory = allCategories.find(
            (c) => c.id !== movedCat.id && c.lockedGlobalPosition === targetPosition
          );

          if (conflictingCategory) {
            // Show conflict dialog
            setDragLockConflictDialog({
              open: true,
              categoryId: movedCat.id,
              categoryName: movedCat.name,
              targetPosition,
              conflictingCategoryName: conflictingCategory.name,
              conflictingCategoryId: conflictingCategory.id,
            });
            return; // Don't complete the drag
          }

          // No conflict - move the locked category globally on ALL tabs
          const homePositions = reassignPositions(allCategories, movedCat.id, targetPosition, 'scrollerPosition', 'showOnHomePage');
          const carouselPositions = reassignPositions(allCategories, movedCat.id, targetPosition, 'carouselPosition', 'showInCarousel');
          const shopPositions = reassignPositions(allCategories, movedCat.id, targetPosition, 'shopPosition', 'showInShop');

          // Track the operation
          trackDragOperation({
            itemId: movedCat.id,
            itemName: movedCat.name,
            section: 'shop',
            positionField: 'shopPosition',
            fromPosition: getLivePosition(movedCat.id, 'shopPosition'),
            toPosition: targetPosition,
            lastMovedAt: new Date().toISOString(),
          });

          // Apply all position changes
          const updatedCategories = allCategories.map((cat) => {
            const updates: Partial<CategoryDraft> = {};

            // Update the locked position
            if (cat.id === movedCat.id) {
              updates.lockedGlobalPosition = targetPosition;
            }

            if (homePositions.has(cat.id)) {
              const pos = homePositions.get(cat.id)!;
              updates.position = pos;
              updates.scrollerPosition = pos;
            }
            if (carouselPositions.has(cat.id)) {
              updates.carouselPosition = carouselPositions.get(cat.id)!;
            }
            if (shopPositions.has(cat.id)) {
              updates.shopPosition = shopPositions.get(cat.id)!;
            }

            return Object.keys(updates).length > 0 ? { ...cat, ...updates } : cat;
          });

          updateCategoryConfig({ categories: updatedCategories });
          return;
        }

        // Non-locked category: normal single-tab reorder
        const reorderedShop = Array.from(shopCategories);
        const [removed] = reorderedShop.splice(source.index, 1);
        reorderedShop.splice(destination.index, 0, removed);

        // Track the operation
        trackDragOperation({
          itemId: movedCat.id,
          itemName: movedCat.name,
          section: 'shop',
          positionField: 'shopPosition',
          fromPosition: getLivePosition(movedCat.id, 'shopPosition'),
          toPosition: destination.index + 1,
          lastMovedAt: new Date().toISOString(),
        });

        // Create map of new shop positions
        const newShopPositions = new Map<string, number>();
        reorderedShop.forEach((cat, index) => {
          newShopPositions.set(cat.id, index + 1);
        });

        // Update only shopPosition for affected categories
        const updatedCategories = allCategories.map((cat) => {
          if (newShopPositions.has(cat.id)) {
            return { ...cat, shopPosition: newShopPositions.get(cat.id)! };
          }
          return cat;
        });

        updateCategoryConfig({ categories: updatedCategories });
      }
    },
    [homeCategories, homeListItems, carouselCategories, shopCategories, updateCategoryConfig, updateCarouselVerticalConfig, updateBrandPositions, activeTab, draft?.categoryConfig?.categories, draft?.carouselVerticalConfig, draft?.brandPositions, liveState?.categoryConfig?.categories, trackDragOperation]
  );

  // Handle banner item drag reorder within a banner position (e.g. slider rotation order)
  const handleBannerDragEnd = useCallback(
    (result: DropResult) => {
      const { source, destination } = result;
      if (!destination || source.index === destination.index) return;

      const positionName = source.droppableId; // e.g. 'slider banner'
      const positions = draft?.bannerConfig?.positions;
      if (!positions) return;

      const posIndex = positions.findIndex((p) => p.name === positionName);
      if (posIndex === -1) return;

      const pos = positions[posIndex];
      const reorderedItems = Array.from(pos.items);
      const [moved] = reorderedItems.splice(source.index, 1);
      reorderedItems.splice(destination.index, 0, moved);

      // Update position field for each item
      const updatedItems = reorderedItems.map((item, i) => ({ ...item, position: i }));
      const updatedPositions = positions.map((p, i) =>
        i === posIndex ? { ...p, items: updatedItems } : p,
      );
      updateBannerConfig({ positions: updatedPositions });

      trackDragOperation({
        itemId: moved.id,
        itemName: moved.name || 'Banner',
        section: 'banners',
        positionField: 'bannerPosition',
        fromPosition: source.index,
        toPosition: destination.index,
        lastMovedAt: new Date().toISOString(),
      });
    },
    [draft?.bannerConfig?.positions, updateBannerConfig, trackDragOperation],
  );

  // Handle category edit - fetch fresh data from API to ensure we have the latest fields

  // Handle New & Popular card click — open config dialog
  const handleNewPopularClick = useCallback(() => {
    setNewPopularDialogOpen(true);
    updatePresence('categories');
  }, [updatePresence]);

  // Handle New & Popular config save
  const handleNewPopularSave = useCallback((config: NewPopularConfig) => {
    setNewPopularDialogOpen(false);
    const currentCategories = draft?.categoryConfig?.categories || [];
    updateCategoryConfig({
      categories: currentCategories,
      newPopularConfig: config,
    });
  }, [draft?.categoryConfig?.categories, updateCategoryConfig]);


  // Handle category visibility toggle with auto-positioning for locked categories
  const handleToggleCategoryVisibility = useCallback(
    (categoryId: string, field: keyof CategoryDraft) => {
      if (!draft?.categoryConfig?.categories) return;

      const category = draft.categoryConfig.categories.find((c) => c.id === categoryId);
      if (!category) return;

      const newValue = !category[field];
      const lockedPosition = category.lockedGlobalPosition;

      // Determine the position field based on visibility field
      const positionFieldMap: Record<string, keyof CategoryDraft> = {
        showOnHomePage: 'scrollerPosition',
        showInCarousel: 'carouselPosition',
        showInShop: 'shopPosition',
      };
      const positionField = positionFieldMap[field];

      // Determine the visibility filter field for counting existing items
      const visibilityFieldMap: Record<string, keyof CategoryDraft> = {
        showOnHomePage: 'showOnHomePage',
        showInCarousel: 'showInCarousel',
        showInShop: 'showInShop',
      };
      const visibilityField = visibilityFieldMap[field];

      // If making visible, locked, and we have a position field
      if (newValue && lockedPosition !== null && lockedPosition !== undefined && positionField && visibilityField) {
        // Shift other visible categories down and insert at locked position
        let categories = draft.categoryConfig.categories.map((cat) => {
          if (cat.id === categoryId) {
            // Set the category to visible and at locked position
            return { ...cat, [field]: true, [positionField]: lockedPosition };
          }
          // Shift other visible categories at or after locked position down by 1
          if (cat.isActive && cat[visibilityField] && (cat[positionField] as number) >= lockedPosition) {
            return { ...cat, [positionField]: (cat[positionField] as number) + 1 };
          }
          return cat;
        });

        updateCategoryConfig({ categories });
      } else {
        // Normal toggle without auto-positioning
        const categories = draft.categoryConfig.categories.map((cat) =>
          cat.id === categoryId ? { ...cat, [field]: !cat[field] } : cat
        );
        updateCategoryConfig({ categories });
      }
    },
    [draft?.categoryConfig?.categories, updateCategoryConfig]
  );

  // Handle position lock toggle - locks/unlocks category at a global position across all screens
  const handleToggleLockPosition = useCallback(
    (categoryId: string, position: number) => {
      if (!draft?.categoryConfig?.categories) return;

      const category = draft.categoryConfig.categories.find((c) => c.id === categoryId);
      if (!category) return;

      const isCurrentlyLocked = category.lockedGlobalPosition !== null && category.lockedGlobalPosition !== undefined;

      if (isCurrentlyLocked) {
        // Unlock: set lockedGlobalPosition to null
        const categories = draft.categoryConfig.categories.map((cat) =>
          cat.id === categoryId ? { ...cat, lockedGlobalPosition: null } : cat
        );
        updateCategoryConfig({ categories });
      } else {
        // Lock: check if another category is already locked at this position
        const conflictingCategory = draft.categoryConfig.categories.find(
          (c) => c.id !== categoryId && c.lockedGlobalPosition === position
        );

        if (conflictingCategory) {
          // Show conflict dialog
          setLockConflictDialog({
            open: true,
            categoryId,
            categoryName: category.name,
            position,
            conflictingCategoryName: conflictingCategory.name,
          });
        } else {
          // No conflict - lock the position and reposition on ALL tabs
          // Helper to reassign positions for a tab, keeping relative order but inserting target at locked position
          const reassignPositions = (
            cats: CategoryDraft[],
            targetId: string,
            lockedPos: number,
            positionField: 'scrollerPosition' | 'carouselPosition' | 'shopPosition',
            visibilityField: 'showOnHomePage' | 'showInCarousel' | 'showInShop'
          ): Map<string, number> => {
            const positionMap = new Map<string, number>();

            // Get visible categories excluding target, sorted by current position
            const visibleOthers = cats
              .filter((c) => c.id !== targetId && c[visibilityField] && c.isActive)
              .sort((a, b) => a[positionField] - b[positionField]);

            // Target gets the locked position
            positionMap.set(targetId, lockedPos);

            // Assign positions to others, skipping the locked position
            let pos = 1;
            for (const cat of visibleOthers) {
              if (pos === lockedPos) pos++; // Skip locked position
              positionMap.set(cat.id, pos);
              pos++;
            }

            return positionMap;
          };

          // Calculate new positions for each tab
          const homePositions = reassignPositions(
            draft.categoryConfig.categories,
            categoryId,
            position,
            'scrollerPosition',
            'showOnHomePage'
          );
          const carouselPositions = reassignPositions(
            draft.categoryConfig.categories,
            categoryId,
            position,
            'carouselPosition',
            'showInCarousel'
          );
          const shopPositions = reassignPositions(
            draft.categoryConfig.categories,
            categoryId,
            position,
            'shopPosition',
            'showInShop'
          );

          // Apply all position changes
          const categories = draft.categoryConfig.categories.map((cat) => {
            const updates: Partial<CategoryDraft> = {};

            if (cat.id === categoryId) {
              updates.lockedGlobalPosition = position;
            }

            // Apply home position if this category is visible on home
            if (homePositions.has(cat.id)) {
              updates.scrollerPosition = homePositions.get(cat.id)!;
            }

            // Apply carousel position if this category is visible on carousel
            if (carouselPositions.has(cat.id)) {
              updates.carouselPosition = carouselPositions.get(cat.id)!;
            }

            // Apply shop position if this category is visible on shop
            if (shopPositions.has(cat.id)) {
              updates.shopPosition = shopPositions.get(cat.id)!;
            }

            return Object.keys(updates).length > 0 ? { ...cat, ...updates } : cat;
          });

          updateCategoryConfig({ categories });
        }
      }
    },
    [draft?.categoryConfig?.categories, updateCategoryConfig]
  );

  // Lock conflict dialog state
  const [lockConflictDialog, setLockConflictDialog] = useState<{
    open: boolean;
    categoryId: string;
    categoryName: string;
    position: number;
    conflictingCategoryName: string;
  } | null>(null);

  // Drag lock conflict dialog state (when dragging a locked category to a position occupied by another locked category)
  const [dragLockConflictDialog, setDragLockConflictDialog] = useState<{
    open: boolean;
    categoryId: string;
    categoryName: string;
    targetPosition: number;
    conflictingCategoryName: string;
    conflictingCategoryId: string;
  } | null>(null);

  // Handle force lock (unlock conflicting category and lock this one)
  const handleForceLock = useCallback(() => {
    if (!lockConflictDialog || !draft?.categoryConfig?.categories) return;

    const { categoryId, position } = lockConflictDialog;

    // Unlock any category at this position and lock the new one
    const categories = draft.categoryConfig.categories.map((cat) => {
      if (cat.lockedGlobalPosition === position) {
        return { ...cat, lockedGlobalPosition: null };
      }
      if (cat.id === categoryId) {
        return { ...cat, lockedGlobalPosition: position };
      }
      return cat;
    });

    updateCategoryConfig({ categories });
    setLockConflictDialog(null);
  }, [lockConflictDialog, draft?.categoryConfig?.categories, updateCategoryConfig]);

  // Handle force drag of locked category (unlock conflicting category and move to new position)
  const handleForceDragLockedCategory = useCallback(() => {
    if (!dragLockConflictDialog || !draft?.categoryConfig?.categories) return;

    const { categoryId, targetPosition, conflictingCategoryId } = dragLockConflictDialog;

    // Helper to reassign positions for a tab, keeping relative order but inserting target at locked position
    const reassignPositions = (
      cats: CategoryDraft[],
      targetId: string,
      lockedPos: number,
      positionField: 'scrollerPosition' | 'carouselPosition' | 'shopPosition',
      visibilityField: 'showOnHomePage' | 'showInCarousel' | 'showInShop'
    ): Map<string, number> => {
      const positionMap = new Map<string, number>();

      // Get visible categories excluding target, sorted by current position
      const visibleOthers = cats
        .filter((c) => c.id !== targetId && c[visibilityField] && c.isActive)
        .sort((a, b) => a[positionField] - b[positionField]);

      // Target gets the locked position
      positionMap.set(targetId, lockedPos);

      // Assign positions to others, skipping the locked position
      let pos = 1;
      for (const cat of visibleOthers) {
        if (pos === lockedPos) pos++; // Skip locked position
        positionMap.set(cat.id, pos);
        pos++;
      }

      return positionMap;
    };

    // Calculate new positions for each tab
    const homePositions = reassignPositions(
      draft.categoryConfig.categories,
      categoryId,
      targetPosition,
      'scrollerPosition',
      'showOnHomePage'
    );
    const carouselPositions = reassignPositions(
      draft.categoryConfig.categories,
      categoryId,
      targetPosition,
      'carouselPosition',
      'showInCarousel'
    );
    const shopPositions = reassignPositions(
      draft.categoryConfig.categories,
      categoryId,
      targetPosition,
      'shopPosition',
      'showInShop'
    );

    // Apply all position changes
    const categories = draft.categoryConfig.categories.map((cat) => {
      const updates: Partial<CategoryDraft> = {};

      // Unlock the conflicting category
      if (cat.id === conflictingCategoryId) {
        updates.lockedGlobalPosition = null;
      }

      // Update the target category's locked position
      if (cat.id === categoryId) {
        updates.lockedGlobalPosition = targetPosition;
      }

      // Apply home position if this category is visible on home
      if (homePositions.has(cat.id)) {
        updates.scrollerPosition = homePositions.get(cat.id)!;
      }

      // Apply carousel position if this category is visible on carousel
      if (carouselPositions.has(cat.id)) {
        updates.carouselPosition = carouselPositions.get(cat.id)!;
      }

      // Apply shop position if this category is visible on shop
      if (shopPositions.has(cat.id)) {
        updates.shopPosition = shopPositions.get(cat.id)!;
      }

      return Object.keys(updates).length > 0 ? { ...cat, ...updates } : cat;
    });

    updateCategoryConfig({ categories });
    setDragLockConflictDialog(null);
  }, [dragLockConflictDialog, draft?.categoryConfig?.categories, updateCategoryConfig]);

  // Lock All dialog state
  const [lockAllDialog, setLockAllDialog] = useState<{
    open: boolean;
    isAllLocations: boolean;
    categoryCount: number;
  } | null>(null);


  // Handle Lock All confirmation
  const handleConfirmLockAll = useCallback(() => {
    if (!lockAllDialog || !draft?.categoryConfig?.categories) return;

    const allCategories = draft.categoryConfig.categories;

    if (lockAllDialog.isAllLocations) {
      // All Locations mode: Lock ALL active categories
      // Make them visible on all tabs and sync positions
      const activeCategories = allCategories
        .filter((c) => c.isActive)
        .sort((a, b) => {
          // Sort by the position field of the current tab (or scrollerPosition as default)
          const posField = activeTab === 'carousel' ? 'carouselPosition' : activeTab === 'shop' ? 'shopPosition' : 'scrollerPosition';
          return a[posField] - b[posField];
        });

      // Create position map - all positions will be the same across tabs
      const positionMap = new Map<string, number>();
      activeCategories.forEach((cat, index) => {
        positionMap.set(cat.id, index + 1);
      });

      // Update all categories
      const updatedCategories = allCategories.map((cat) => {
        if (!cat.isActive) return cat;

        const newPosition = positionMap.get(cat.id);
        if (newPosition === undefined) return cat;

        return {
          ...cat,
          // Make visible on all tabs
          showOnHomePage: true,
          showInCarousel: true,
          showInCarouselWeb: true,
          showInShop: true,
          // Sync all positions
          position: newPosition,
          scrollerPosition: newPosition,
          carouselPosition: newPosition,
          shopPosition: newPosition,
          // Lock at this position
          lockedGlobalPosition: newPosition,
        };
      });

      updateCategoryConfig({ categories: updatedCategories });
    } else {
      // Specific store view: Lock visible categories on current tab
      // Get the current tab's categories (already sorted by their position)
      const currentTabCategories = activeTab === 'home'
        ? homeCategories
        : activeTab === 'carousel'
          ? carouselCategories
          : shopCategories;

      // Create position map from current tab order
      const positionMap = new Map<string, number>();
      currentTabCategories.forEach((cat, index) => {
        positionMap.set(cat.id, index + 1);
      });

      // Update categories that are visible on current tab
      const updatedCategories = allCategories.map((cat) => {
        const newPosition = positionMap.get(cat.id);
        if (newPosition === undefined) return cat;

        return {
          ...cat,
          // Sync all positions to match current tab order
          position: newPosition,
          scrollerPosition: newPosition,
          carouselPosition: newPosition,
          shopPosition: newPosition,
          // Lock at this position
          lockedGlobalPosition: newPosition,
        };
      });

      updateCategoryConfig({ categories: updatedCategories });
    }

    setLockAllDialog(null);
  }, [lockAllDialog, draft?.categoryConfig?.categories, activeTab, homeCategories, carouselCategories, shopCategories, updateCategoryConfig]);

  // Handle Unlock All
  const handleUnlockAll = useCallback(() => {
    if (!draft?.categoryConfig?.categories) return;

    const updatedCategories = draft.categoryConfig.categories.map((cat) => ({
      ...cat,
      lockedGlobalPosition: null,
    }));

    updateCategoryConfig({ categories: updatedCategories });
  }, [draft?.categoryConfig?.categories, updateCategoryConfig]);


  // Handle category update from AllCategoriesTab
  const handleCategoryUpdateFromAllTab = useCallback(
    (categoryId: string, updates: Partial<CategoryDraft>) => {
      if (!draft?.categoryConfig?.categories) return;

      const categories = draft.categoryConfig.categories.map((cat) =>
        cat.id === categoryId ? { ...cat, ...updates } : cat
      );

      updateCategoryConfig({ categories });
    },
    [draft?.categoryConfig?.categories, updateCategoryConfig]
  );

  // Handle banner item toggle
  const handleToggleBannerItem = useCallback(
    (positionName: string, itemId: string) => {
      if (!draft?.bannerConfig?.positions) return;

      const positions = draft.bannerConfig.positions.map((pos) => {
        if (pos.name !== positionName) return pos;
        return {
          ...pos,
          items: pos.items.map((item) =>
            item.id === itemId ? { ...item, isActive: !item.isActive } : item
          ),
        };
      });

      updateBannerConfig({ positions });
    },
    [draft?.bannerConfig?.positions, updateBannerConfig]
  );

  // Handle banner item edit
  const handleEditBannerItem = useCallback(
    (positionName: string, itemId: string) => {
      const position = draft?.bannerConfig?.positions?.find((p) => p.name === positionName);
      const item = position?.items?.find((i) => i.id === itemId);
      if (position && item) {
        setCurrentBannerPosition(position);
        setEditingBannerItem(item);
        setBannerDialogOpen(true);
        updatePresence('banners', itemId);
      }
    },
    [draft?.bannerConfig?.positions, updatePresence]
  );

  // Handle add new banner item
  const handleAddBannerItem = useCallback(
    (position: BannerPositionDraft) => {
      setCurrentBannerPosition(position);
      setEditingBannerItem(null);
      setBannerDialogOpen(true);
      updatePresence('banners');
    },
    [updatePresence]
  );

  // Handle banner dialog close
  const handleBannerDialogClose = useCallback(() => {
    setBannerDialogOpen(false);
    setEditingBannerItem(null);
    setCurrentBannerPosition(null);
  }, []);

  // Handle banner draft save (create/edit through draft, not live API)
  const handleBannerDraftSave = useCallback(
    async (data: BannerItemDraftSaveData) => {
      if (!draft?.bannerConfig?.positions || !currentBannerPosition) return;

      const positions = draft.bannerConfig.positions.map((pos) => {
        if (pos.name !== currentBannerPosition.name) return pos;

        if (editingBannerItem) {
          // EDIT: replace item in-place, preserve position
          return {
            ...pos,
            items: pos.items.map((item) =>
              item.id === editingBannerItem.id
                ? { ...item, ...data, position: item.position }
                : item
            ),
          };
        } else {
          // CREATE: append with next position number
          const nextPos =
            pos.items.length > 0
              ? Math.max(...pos.items.map((i) => i.position)) + 1
              : 0;
          const newItem: BannerItemDraft = {
            ...data,
            position: nextPos,
          };
          return { ...pos, items: [...pos.items, newItem] };
        }
      });

      await updateBannerConfig({ positions });
      setBannerDialogOpen(false);
      setEditingBannerItem(null);
      setCurrentBannerPosition(null);
    },
    [draft?.bannerConfig?.positions, currentBannerPosition, editingBannerItem, updateBannerConfig]
  );

  // Handle banner item delete (request confirmation)
  const handleRequestDeleteBannerItem = useCallback(
    (positionName: string, itemId: string) => {
      const position = draft?.bannerConfig?.positions?.find((p) => p.name === positionName);
      const item = position?.items?.find((i) => i.id === itemId);
      if (item) {
        setDeleteBannerTarget({ positionName, item });
        setDeleteBannerConfirmOpen(true);
      }
    },
    [draft?.bannerConfig?.positions]
  );

  // Confirm banner item delete
  const handleConfirmDeleteBannerItem = useCallback(() => {
    if (!deleteBannerTarget || !draft?.bannerConfig?.positions) return;

    const positions = draft.bannerConfig.positions.map((pos) => {
      if (pos.name !== deleteBannerTarget.positionName) return pos;
      return {
        ...pos,
        items: pos.items.filter((item) => item.id !== deleteBannerTarget.item.id),
      };
    });

    updateBannerConfig({ positions });
    setDeleteBannerConfirmOpen(false);
    setDeleteBannerTarget(null);
  }, [deleteBannerTarget, draft?.bannerConfig?.positions, updateBannerConfig]);

  // Loading state
  if (isLoading && !draft) {
    return (
      <Paper sx={{ p: 2 }}>
        <Skeleton variant="text" width={150} height={24} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={48} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={48} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={48} sx={{ mb: 2 }} />
        <Skeleton variant="text" width={150} height={24} sx={{ mb: 2 }} />
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} variant="rounded" height={64} sx={{ mb: 1 }} />
        ))}
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 0, position: 'relative', display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Saving indicator */}
      {isSaving && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            borderRadius: '4px 4px 0 0',
            zIndex: 20,
          }}
        />
      )}

      {/* Sticky Header with Store Selector and Tabs */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 15,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          pt: 2,
        }}
      >
        {/* Store Selector. Hidden when the page renders it beside the preview —
            it only ever filtered the preview, never the layout. */}
        <Box sx={{ display: hideStoreSelector ? 'none' : 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel>Preview As</InputLabel>
            <Select
              value={storesLoading ? '' : (selectedStoreId || '')}
              onChange={(e) => setSelectedStoreId(e.target.value || null)}
              label="Preview As"
              disabled={storesLoading}
            >
              <MenuItem value="">
                <em>Guest</em>
              </MenuItem>
              <MenuItem value={ALL_LOCATIONS}>
                <em>All Locations (Combined)</em>
              </MenuItem>
              {stores.map((store) => (
                <MenuItem key={store.id} value={store.id}>
                  {store.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {countsLoading && (
            <Tooltip title="Loading product counts...">
              <CircularProgress size={20} />
            </Tooltip>
          )}
          {selectedStoreId === ALL_LOCATIONS && !countsLoading && (
            <Chip
              label="All Locations"
              size="small"
              color="secondary"
              variant="outlined"
              sx={{ display: { xs: 'none', md: 'flex' } }}
            />
          )}
          {selectedStore && selectedStoreId !== ALL_LOCATIONS && !countsLoading && (
            <Chip
              label={selectedStore.name}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ display: { xs: 'none', md: 'flex' } }}
            />
          )}
          {!selectedStoreId && !storesLoading && (
            <Chip
              label="Guest"
              size="small"
              color="default"
              variant="outlined"
              sx={{ display: { xs: 'none', md: 'flex' } }}
            />
          )}
        </Box>

        {/* Removed: the category search box, the "Group empty categories at bottom"
            toggle with its info banner, and the Lock All / Unlock All buttons.

            Between them they occupied most of the panel above the list you actually
            came to edit, for three things that are rarely used: the list is short
            enough to scan, grouping empties is moot now that empty categories are
            hidden by default, and locking is a per-category action that did not need
            a bulk control sitting permanently on screen. */}

        {/* Tab Navigation — suppressed once the page has navigated into a
            section, where it would just repeat the hub you arrived from. */}
        {!hideTabs && (
        <Tabs
          value={activeTab}
          onChange={(_, newValue: ConfigTab) => { setCategorySearchQuery(''); setActiveTab(newValue); }}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Configuration tabs"
          sx={{
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.75rem',
              minWidth: 'auto',
              px: 1.5,
              overflow: 'visible',
            },
          }}
        >
          <Tab
            value="home"
            label={
              <Badge badgeContent={tabCounts.homeCount} color="primary" max={99}
                sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16, px: '4px', right: -10, top: -2 } }}>
                <Box sx={{ pr: 1 }}>Home</Box>
              </Badge>
            }
            icon={<ReorderIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            aria-label="Home layout configuration"
          />
          <Tab
            value="carousel"
            label={
              <Badge badgeContent={tabCounts.carouselCount} color="primary" max={99}
                sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16, px: '4px', right: -10, top: -2 } }}>
                <Box sx={{ pr: 1 }}>Carousel</Box>
              </Badge>
            }
            icon={<ViewCarouselIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            aria-label="Carousel configuration"
          />
          <Tab
            value="shop"
            label={
              <Badge badgeContent={tabCounts.shopCount} color="primary" max={99}
                sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16, px: '4px', right: -10, top: -2 } }}>
                <Box sx={{ pr: 1 }}>Shop</Box>
              </Badge>
            }
            icon={<StorefrontIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            aria-label="Shop page configuration"
          />
          <Tab
            value="brands"
            label={
              <Badge badgeContent={tabCounts.brandsCount} color="primary" max={99}
                sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16, px: '4px', right: -10, top: -2 } }}>
                <Box sx={{ pr: 1 }}>Brands</Box>
              </Badge>
            }
            icon={<BrandsIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            aria-label="Brands configuration"
          />
          <Tab
            value="all"
            label={
              <Badge badgeContent={tabCounts.allCount} color="default" max={99}
                sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16, px: '4px', right: -10, top: -2 } }}>
                <Box sx={{ pr: 1 }}>All</Box>
              </Badge>
            }
            icon={<CategoryIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            aria-label="All categories"
          />
          <Tab
            value="banners"
            label={
              <Badge badgeContent={tabCounts.bannersCount} color="primary" max={99}
                sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16, px: '4px', right: -10, top: -2 } }}>
                <Box sx={{ pr: 1 }}>Banners</Box>
              </Badge>
            }
            icon={<ImageIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            aria-label="Banner configuration"
          />
        </Tabs>
        )}

      </Box>

      {/* Scrollable Content Area */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {/* Banners Tab Content */}
        {activeTab === 'banners' && (
          <Box>
            {/* Banner Sections */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2,
                  p: 1.5,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #FF002C 0%, #CC0023 100%)',
                  boxShadow: '0 4px 12px rgba(255, 0, 44, 0.25)',
                }}
              >
                <ImageIcon sx={{ color: 'white', fontSize: 20 }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    color: 'white',
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                  }}
                >
                  Banners
                </Typography>
              </Box>

              {draft?.bannerConfig?.positions?.map((position) => (
                <Box key={position.name} sx={{ mb: 1 }}>
                  <Paper variant="outlined">
                    <BannerSectionHeader
                      position={position}
                      expanded={expandedBanners[position.name] ?? false}
                      onToggle={() => toggleBannerSection(position.name)}
                    />
                    <Collapse in={expandedBanners[position.name]}>
                      <Box sx={{ px: 2, pb: 2 }}>
                        {position.items.length === 0 ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ py: 2, textAlign: 'center' }}
                          >
                            No banner items
                          </Typography>
                        ) : position.type === 'slider' && position.items.length > 1 ? (
                          <DragDropContext onDragEnd={handleBannerDragEnd}>
                            <Droppable droppableId={position.name}>
                              {(droppableProvided: DroppableProvided) => (
                                <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                                  {[...position.items].sort((a, b) => a.position - b.position).map((item, itemIndex) => (
                                    <Draggable key={item.id} draggableId={item.id} index={itemIndex}>
                                      {(dragProv: DraggableProvided, dragSnap: DraggableStateSnapshot) => (
                                        <div ref={dragProv.innerRef} {...dragProv.draggableProps}>
                                          <BannerItemCard
                                            item={item}
                                            isDragging={dragSnap.isDragging}
                                            dragHandleProps={dragProv.dragHandleProps}
                                            onEdit={() => handleEditBannerItem(position.name, item.id)}
                                            onToggleActive={() => handleToggleBannerItem(position.name, item.id)}
                                            onDelete={() => handleRequestDeleteBannerItem(position.name, item.id)}
                                          />
                                        </div>
                                      )}
                                    </Draggable>
                                  ))}
                                  {droppableProvided.placeholder}
                                </div>
                              )}
                            </Droppable>
                          </DragDropContext>
                        ) : (
                          position.items.map((item) => (
                            <BannerItemCard
                              key={item.id}
                              item={item}
                              onEdit={() => handleEditBannerItem(position.name, item.id)}
                              onToggleActive={() =>
                                handleToggleBannerItem(position.name, item.id)
                              }
                              onDelete={() =>
                                handleRequestDeleteBannerItem(position.name, item.id)
                              }
                            />
                          ))
                        )}
                        <Button
                          startIcon={<AddIcon />}
                          size="small"
                          variant="outlined"
                          fullWidth
                          sx={{ mt: 1 }}
                          onClick={() => handleAddBannerItem(position)}
                        >
                          Add Banner
                        </Button>
                      </Box>
                    </Collapse>
                  </Paper>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Home Order Tab Content */}
        {activeTab === 'home' && (
          <>
            {/* Categories Section */}
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                  p: 1.5,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #FF002C 0%, #CC0023 100%)',
                  boxShadow: '0 4px 12px rgba(255, 0, 44, 0.25)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CategoryIcon sx={{ color: 'white', fontSize: 20 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: '0.05em',
                      color: 'white',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                    }}
                  >
                    Categories
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setCategoriesExpanded(!categoriesExpanded)}
                  sx={{ color: 'white' }}
                >
                  {categoriesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={categoriesExpanded}>
                {/* Active Categories + N&P + Carousel + Brand Scroller - Draggable list */}
                <DragDropContext onDragStart={handleDragStart} onDragEnd={handleCategoryDragEnd}>
                  <Droppable droppableId="categories">
                    {(provided: DroppableProvided) => {
                      let categoryPosition = 1; // Track category-only position for display
                      return (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {filteredHomeListItems.map((item, index) => (
                            <Draggable
                              key={item.id}
                              draggableId={item.id}
                              index={index}
                              isDragDisabled={item.type === 'new-popular' || isSearchActive}
                            >
                              {(
                                dragProvided: DraggableProvided,
                                snapshot: DraggableStateSnapshot
                              ) => {
                                if (item.type === 'new-popular') {
                                  const npConfig = draft?.categoryConfig?.newPopularConfig;
                                  const npName = npConfig?.name || 'New & Popular';
                                  const npFilter = npConfig?.homeFilterType || 'newOrPopular';
                                  const filterLabel = npFilter === 'newOrPopular' ? 'Auto' :
                                    npFilter === 'tag' ? `Tag: ${npConfig?.homeFilterValue || ''}` :
                                    npFilter === 'brand' ? `Brand: ${npConfig?.homeFilterValue || ''}` :
                                    npFilter === 'combined' ? 'Combined' :
                                    npFilter === 'category' ? 'Category' : npFilter;
                                  return (
                                    <div
                                      ref={dragProvided.innerRef}
                                      {...dragProvided.draggableProps}
                                    >
                                      <Tooltip
                                        title="Click to configure which products appear in this block. Always appears first on the home screen."
                                        placement="top"
                                        arrow
                                      >
                                        <Card
                                          variant="outlined"
                                          onClick={handleNewPopularClick}
                                          sx={{
                                            mb: 1,
                                            border: '1px solid',
                                            borderColor: 'warning.main',
                                            background: alpha('#ed6c02', 0.04),
                                            cursor: 'pointer',
                                            '&:hover': {
                                              background: alpha('#ed6c02', 0.08),
                                              borderColor: 'warning.dark',
                                            },
                                          }}
                                        >
                                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                              <Box sx={{ display: 'flex', alignItems: 'center', color: 'warning.main' }}>
                                                <LockIcon fontSize="small" />
                                              </Box>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  width: 24, height: 24,
                                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                  borderRadius: '50%', bgcolor: 'warning.main', color: 'white', fontWeight: 600,
                                                }}
                                              >
                                                1
                                              </Typography>
                                              <Box
                                                sx={{
                                                  width: 48, height: 48,
                                                  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                                                  borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                                }}
                                              >
                                                <TrendingUpIcon sx={{ fontSize: 28, color: 'white' }} />
                                              </Box>
                                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" fontWeight={600} noWrap>{npName}</Typography>
                                                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                                  <Chip icon={<HomeIcon />} label="Home" size="small" color="warning" sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: 10 } }} />
                                                  <Chip label={filterLabel} size="small" variant="outlined" sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: 10 } }} />
                                                </Box>
                                              </Box>
                                              <EditIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                            </Box>
                                          </CardContent>
                                        </Card>
                                      </Tooltip>
                                    </div>
                                  );
                                }
                                if (item.type === 'carousel') {
                                  return (
                                    <PortalAwareDraggable
                                      provided={dragProvided}
                                      snapshot={snapshot}
                                    >
                                      <CarouselPlaceholderCard
                                        isDragging={snapshot.isDragging}
                                        dragHandleProps={dragProvided.dragHandleProps}
                                      />
                                    </PortalAwareDraggable>
                                  );
                                }
                                if (item.type === 'brand-scroller') {
                                  return (
                                    <PortalAwareDraggable
                                      provided={dragProvided}
                                      snapshot={snapshot}
                                    >
                                      <BrandScrollerPlaceholderCard
                                        isDragging={snapshot.isDragging}
                                        dragHandleProps={dragProvided.dragHandleProps}
                                      />
                                    </PortalAwareDraggable>
                                  );
                                }
                                // Category item
                                const catPos = categoryPosition++;
                                return (
                                  <PortalAwareDraggable
                                    provided={dragProvided}
                                    snapshot={snapshot}
                                  >
                                    <CategoryItemCardDraft
                                      category={item.category!}
                                      position={catPos + 1}
                                      lockPosition={catPos}
                                      isDragging={snapshot.isDragging}
                                      dragHandleProps={dragProvided.dragHandleProps}
                                      onToggleCategoryVisibility={handleToggleCategoryVisibility}
                                      onViewProducts={handleViewCategoryProducts}
                                      onToggleLockPosition={handleToggleLockPosition}
                                      productCount={categoryProductCounts[item.category!.id]}
                                      hideStockInfo={isAllLocations}
                                    />
                                  </PortalAwareDraggable>
                                );
                              }}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      );
                    }}
                  </Droppable>
                </DragDropContext>

                {/* Search active hint */}
                {isSearchActive && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                    Clear search to reorder
                  </Typography>
                )}

                {/* Hidden categories section (only when groupEmptyAtBottom is ON and there are empty categories) */}
                {filteredHiddenHomeCategories.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Box
                      onClick={() => setEmptyCollapsed(!emptyCollapsed)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: alpha('#ff9800', 0.06),
                        border: '1px dashed',
                        borderColor: 'warning.light',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: alpha('#ff9800', 0.1) },
                      }}
                    >
                      <VisibilityOffIcon fontSize="small" sx={{ color: 'warning.main' }} />
                      <Typography variant="caption" color="warning.main" fontWeight={600} sx={{ flex: 1 }}>
                        {filteredHiddenHomeCategories.length} empty at {selectedStore?.name || 'this store'}
                      </Typography>
                      {emptyCollapsed ? <ExpandMoreIcon fontSize="small" sx={{ color: 'warning.main' }} /> : <ExpandLessIcon fontSize="small" sx={{ color: 'warning.main' }} />}
                    </Box>

                    <Collapse in={!emptyCollapsed}>
                    <Box sx={{ mt: 1 }}>
                        {filteredHiddenHomeCategories.map((category) => (
                          <Card
                            key={category.id}
                            variant="outlined"
                            sx={{
                              opacity: 0.5,
                              mb: 0.5,
                              border: '1px solid',
                              borderColor: 'warning.light',
                              background: alpha('#ff9800', 0.04),
                            }}
                          >
                            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <VisibilityOffIcon fontSize="small" sx={{ color: 'warning.main' }} />
                                {/* Category image */}
                                <Box
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    backgroundColor: '#FF002C',
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    flexShrink: 0,
                                  }}
                                >
                                  {category.imageUrl ? (
                                    <img
                                      src={category.imageUrl}
                                      alt={category.name}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                  ) : (
                                    <CategoryIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.7)' }} />
                                  )}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={500} noWrap>
                                    {category.name}
                                  </Typography>
                                  <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.6rem' }}>
                                    No products at this store
                                  </Typography>
                                </Box>
                                <Chip
                                  label={0}
                                  size="small"
                                  variant="filled"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    minWidth: 28,
                                    backgroundColor: '#ED6C02',
                                    color: '#FFFFFF',
                                    '& .MuiChip-label': { color: '#FFFFFF' },
                                  }}
                                />
                                <Tooltip title="View products at other stores">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewCategoryProducts(category.id, category.name);
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                )}

                {/* Available to add: active, but the Home tab tick is off */}
                {availableHomeCategories.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Box
                      onClick={() => setAvailableCollapsed(!availableCollapsed)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: alpha('#1976d2', 0.06),
                        border: '1px dashed',
                        borderColor: 'info.light',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: alpha('#1976d2', 0.1) },
                      }}
                    >
                      <AddIcon fontSize="small" sx={{ color: 'info.main' }} />
                      <Typography variant="caption" color="info.main" fontWeight={600} sx={{ flex: 1 }}>
                        {availableHomeCategories.length} available to add
                      </Typography>
                      {availableCollapsed ? <ExpandMoreIcon fontSize="small" sx={{ color: 'info.main' }} /> : <ExpandLessIcon fontSize="small" sx={{ color: 'info.main' }} />}
                    </Box>
                    <Collapse in={!availableCollapsed}>
                    <Box sx={{ mt: 1 }}>
                      {availableHomeCategories.map((category) => (
                        <Card
                          key={category.id}
                          variant="outlined"
                          sx={{
                            opacity: 0.7,
                            mb: 0.5,
                            border: '1px solid',
                            borderColor: 'info.light',
                            background: alpha('#1976d2', 0.03),
                            '&:hover': { opacity: 1, borderColor: 'info.main' },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box
                                sx={{
                                  width: 36, height: 36,
                                  backgroundColor: '#FF002C', borderRadius: 1,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  overflow: 'hidden', flexShrink: 0,
                                }}
                              >
                                {category.imageUrl ? (
                                  <img src={category.imageUrl} alt={category.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  <CategoryIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.7)' }} />
                                )}
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={500} noWrap>{category.name}</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, flexWrap: 'wrap', alignItems: 'center' }}>
                                  <Typography variant="caption" color="info.main" sx={{ fontSize: '0.6rem', mr: 0.5 }}>
                                    {(categoryProductCounts[category.id] ?? -1) >= 0 ? `${categoryProductCounts[category.id]} products` : ''}
                                  </Typography>
                                  {category.showInCarousel && (
                                    <Chip icon={<ViewCarouselIcon />} label="Carousel" size="small" variant="outlined" sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: 9 }, '& .MuiChip-icon': { fontSize: 12 } }} />
                                  )}
                                  {category.showInShop && (
                                    <Chip icon={<StorefrontIcon />} label="Shop" size="small" variant="outlined" sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: 9 }, '& .MuiChip-icon': { fontSize: 12 } }} />
                                  )}
                                </Box>
                              </Box>
                              {(categoryProductCounts[category.id] ?? -1) >= 0 && (
                                <Chip
                                  label={categoryProductCounts[category.id]}
                                  size="small"
                                  variant="filled"
                                  sx={{
                                    height: 18, fontSize: '0.65rem', minWidth: 28,
                                    backgroundColor: '#1976d2', color: '#fff',
                                    '& .MuiChip-label': { color: '#fff' },
                                  }}
                                />
                              )}
                              <Tooltip title="View products">
                                <IconButton size="small" onClick={() => handleViewCategoryProducts(category.id, category.name)}>
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Add to Home page">
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => handleToggleCategoryVisibility(category.id, 'showOnHomePage')}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                    </Collapse>
                  </Box>
                )}

                {homeCategories.length === 0 && hiddenHomeCategories.length === 0 && inactiveCategories.length === 0 && (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 4,
                      px: 2,
                    }}
                  >
                    <CategoryIcon
                      sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      No categories configured
                    </Typography>
                  </Box>
                )}

                {homeCategories.length === 0 && hiddenHomeCategories.length === 0 && inactiveCategories.length > 0 && (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 2,
                      px: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No active categories with home page visibility. Check the "All" tab to enable categories.
                    </Typography>
                  </Box>
                )}

              </Collapse>
            </Box>
          </>
        )}

        {/* Carousel Tab Content */}
        {activeTab === 'carousel' && (
          <Box>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #ef5350 0%, #d32f2f 100%)',
                boxShadow: '0 4px 12px rgba(239, 83, 80, 0.25)',
              }}
            >
              <ViewCarouselIcon sx={{ color: 'white', fontSize: 20 }} />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  color: 'white',
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  flex: 1,
                }}
              >
                Carousel Icons ({carouselCategories.length})
              </Typography>
              <Tooltip title='Categories with "Show in Carousel" enabled'>
                <InfoIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 18 }} />
              </Tooltip>
            </Box>

            {/* Description */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 1 }}>
              Drag to reorder category icons in the carousel (same order on mobile and web).
              Position changes are independent from Home Order.
            </Typography>

            {/* Draggable Category List */}
            {filteredCarouselCategories.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <ViewCarouselIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No categories enabled for carousel yet.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Categories come from Vend.
                </Typography>
              </Box>
            ) : (
              <DragDropContext onDragStart={handleDragStart} onDragEnd={handleCategoryDragEnd}>
                <Droppable droppableId="carousel-categories">
                  {(provided: DroppableProvided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {filteredCarouselCategories.map((category, index) => (
                        <React.Fragment key={category.id}>
                          <Draggable
                            draggableId={`carousel-${category.id}`}
                            index={index}
                            isDragDisabled={isSearchActive}
                          >
                            {(dragProvided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
                              const productCount = categoryProductCounts[category.id];
                              const hasNoProducts = !isAllLocations && productCount === 0;
                              const isLocked = category.lockedGlobalPosition !== null && category.lockedGlobalPosition !== undefined;
                              return (
                                <PortalAwareDraggable provided={dragProvided} snapshot={snapshot}>
                                  <Card
                                    variant="outlined"
                                    sx={{
                                      opacity: hasNoProducts ? 0.5 : 1,
                                      transition: snapshot.isDragging ? 'none' : 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                      cursor: 'default',
                                      border: '1px solid',
                                      borderColor: snapshot.isDragging ? 'primary.main' : hasNoProducts ? 'warning.light' : 'divider',
                                      background: snapshot.isDragging
                                        ? alpha('#1976d2', 0.04)
                                        : hasNoProducts
                                          ? alpha('#ff9800', 0.04)
                                          : 'background.paper',
                                      '&:hover': snapshot.isDragging ? {} : {
                                        borderColor: hasNoProducts ? 'warning.main' : 'primary.light',
                                        boxShadow: hasNoProducts
                                          ? '0 4px 12px rgba(255, 152, 0, 0.12)'
                                          : '0 4px 12px rgba(99, 102, 241, 0.12)',
                                        transform: 'translateY(-2px)',
                                      },
                                    }}
                                  >
                                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        {/* Drag handle */}
                                        <Box
                                          {...dragProvided.dragHandleProps}
                                          sx={{
                                            cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: snapshot.isDragging ? 'primary.main' : 'action.active',
                                          }}
                                        >
                                          <DragIndicatorIcon />
                                        </Box>

                                        {/* Position number */}
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            width: 24,
                                            height: 24,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '50%',
                                            bgcolor: 'error.main',
                                            color: 'white',
                                            fontWeight: 600,
                                          }}
                                        >
                                          {index + 1}
                                        </Typography>

                                        {/* Category image */}
                                        <Box
                                          sx={{
                                            width: 40,
                                            height: 40,
                                            backgroundColor: '#FF002C',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                            flexShrink: 0,
                                          }}
                                        >
                                          {category.imageUrl ? (
                                            <img
                                              src={category.imageUrl}
                                              alt={category.name}
                                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                          ) : (
                                            <CategoryIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.7)' }} />
                                          )}
                                        </Box>

                                        {/* Category name */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" fontWeight={500} noWrap>
                                              {category.name}
                                            </Typography>
                                            {!isAllLocations && productCount !== undefined && (
                                              <Chip
                                                label={productCount}
                                                size="small"
                                                variant="filled"
                                                sx={{
                                                  height: 18,
                                                  fontSize: '0.65rem',
                                                  minWidth: 28,
                                                  backgroundColor: productCount > 0 ? '#2E7D32' : '#ED6C02',
                                                  color: '#FFFFFF',
                                                  '& .MuiChip-label': { color: '#FFFFFF' },
                                                }}
                                              />
                                            )}
                                          </Box>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
                                            <Chip
                                              icon={<ViewCarouselIcon />}
                                              label="Carousel"
                                              size="small"
                                              color="error"
                                              sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: 9 }, '& .MuiChip-icon': { fontSize: 12 } }}
                                            />
                                            {category.showTagFilters && (
                                              <Chip
                                                icon={<FilterListIcon />}
                                                label="Filters"
                                                size="small"
                                                color="secondary"
                                                variant="outlined"
                                                sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: 9 }, '& .MuiChip-icon': { fontSize: 12 } }}
                                              />
                                            )}
                                            {!isAllLocations && hasNoProducts && (
                                              <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.6rem' }}>
                                                No products
                                              </Typography>
                                            )}
                                          </Box>
                                        </Box>

                                        {/* Lock position button */}
                                        <Tooltip title={isLocked ? `Locked at position ${category.lockedGlobalPosition} (click to unlock)` : `Lock position ${index + 1} across all screens`}>
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleToggleLockPosition(category.id, index + 1);
                                            }}
                                            sx={{
                                              color: isLocked ? 'warning.main' : 'action.disabled',
                                            }}
                                          >
                                            {isLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                                          </IconButton>
                                        </Tooltip>

                                        {/* View products button - hidden in All Locations mode */}
                                        {!isAllLocations && (
                                          <Tooltip title="View products">
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewCategoryProducts(category.id, category.name);
                                              }}
                                            >
                                              <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}

                                      {/* Edit removed — categories are edited in
                                          the All Categories tab, against the draft. */}
                                      </Box>
                                    </CardContent>
                                  </Card>
                                </PortalAwareDraggable>
                              );
                            }}
                          </Draggable>

                          {/* Mobile viewport indicator (after position 4) */}
                          {index === 3 && carouselCategories.length > 4 && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                my: 1.5,
                                px: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  flex: 1,
                                  height: 0,
                                  borderTop: '1px dashed',
                                  borderColor: 'grey.400',
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'grey.500',
                                  fontSize: '0.65rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Mobile viewport
                              </Typography>
                              <Box
                                sx={{
                                  flex: 1,
                                  height: 0,
                                  borderTop: '1px dashed',
                                  borderColor: 'grey.400',
                                }}
                              />
                            </Box>
                          )}

                          {/* Web viewport / recommended limit indicator (after position 8) */}
                          {index === 7 && carouselCategories.length > 8 && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                my: 1.5,
                                px: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  flex: 1,
                                  height: 0,
                                  borderTop: '1px dashed',
                                  borderColor: 'info.light',
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'info.main',
                                  fontSize: '0.65rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Web viewport · Recommended limit
                              </Typography>
                              <Box
                                sx={{
                                  flex: 1,
                                  height: 0,
                                  borderTop: '1px dashed',
                                  borderColor: 'info.light',
                                }}
                              />
                            </Box>
                          )}
                        </React.Fragment>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}

            {/* Search active hint */}
            {isSearchActive && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                Clear search to reorder
              </Typography>
            )}

            {/* Hidden carousel categories section (only when groupEmptyAtBottom is ON) */}
            {filteredHiddenCarouselCategories.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Box
                  onClick={() => setEmptyCollapsed(!emptyCollapsed)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: alpha('#ff9800', 0.06),
                    border: '1px dashed',
                    borderColor: 'warning.light',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha('#ff9800', 0.1) },
                  }}
                >
                  <VisibilityOffIcon fontSize="small" sx={{ color: 'warning.main' }} />
                  <Typography variant="caption" color="warning.main" fontWeight={600} sx={{ flex: 1 }}>
                    {filteredHiddenCarouselCategories.length} empty at {selectedStore?.name || 'this store'}
                  </Typography>
                  {emptyCollapsed ? <ExpandMoreIcon fontSize="small" sx={{ color: 'warning.main' }} /> : <ExpandLessIcon fontSize="small" sx={{ color: 'warning.main' }} />}
                </Box>
                <Collapse in={!emptyCollapsed}>
                <Box sx={{ mt: 1 }}>
                  {filteredHiddenCarouselCategories.map((category) => (
                    <Card
                      key={category.id}
                      variant="outlined"
                      sx={{
                        opacity: 0.5,
                        mb: 0.5,
                        border: '1px solid',
                        borderColor: 'warning.light',
                        background: alpha('#ff9800', 0.04),
                      }}
                    >
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <VisibilityOffIcon fontSize="small" sx={{ color: 'warning.main' }} />
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              backgroundColor: '#FF002C',
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              flexShrink: 0,
                            }}
                          >
                            {category.imageUrl ? (
                              <img
                                src={category.imageUrl}
                                alt={category.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <CategoryIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.7)' }} />
                            )}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={500} noWrap>
                              {category.name}
                            </Typography>
                            <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.6rem' }}>
                              No products at this store
                            </Typography>
                          </Box>
                          <Chip
                            label={0}
                            size="small"
                            variant="filled"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              minWidth: 28,
                              backgroundColor: '#ED6C02',
                              color: '#FFFFFF',
                            }}
                          />
                          <Tooltip title="View products at other stores">
                            <IconButton
                              size="small"
                              onClick={() => handleViewCategoryProducts(category.id, category.name)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
                </Collapse>
              </Box>
            )}

            {/* Available to add: active, but the Carousel tab tick is off */}
            {availableCarouselCategories.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Box
                  onClick={() => setAvailableCollapsed(!availableCollapsed)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 1, py: 0.5, borderRadius: 1,
                    bgcolor: alpha('#1976d2', 0.06),
                    border: '1px dashed', borderColor: 'info.light',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha('#1976d2', 0.1) },
                  }}
                >
                  <AddIcon fontSize="small" sx={{ color: 'info.main' }} />
                  <Typography variant="caption" color="info.main" fontWeight={600} sx={{ flex: 1 }}>
                    {availableCarouselCategories.length} available to add
                  </Typography>
                  {availableCollapsed ? <ExpandMoreIcon fontSize="small" sx={{ color: 'info.main' }} /> : <ExpandLessIcon fontSize="small" sx={{ color: 'info.main' }} />}
                </Box>
                <Collapse in={!availableCollapsed}>
                <Box sx={{ mt: 1 }}>
                  {availableCarouselCategories.map((category) => (
                    <Card
                      key={category.id}
                      variant="outlined"
                      sx={{
                        opacity: 0.7, mb: 0.5,
                        border: '1px solid', borderColor: 'info.light',
                        background: alpha('#1976d2', 0.03),
                        '&:hover': { opacity: 1, borderColor: 'info.main' },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ width: 36, height: 36, backgroundColor: '#FF002C', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                            {category.imageUrl ? (
                              <img src={category.imageUrl} alt={category.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <CategoryIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.7)' }} />
                            )}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={500} noWrap>{category.name}</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, flexWrap: 'wrap', alignItems: 'center' }}>
                              <Typography variant="caption" color="info.main" sx={{ fontSize: '0.6rem', mr: 0.5 }}>
                                {(categoryProductCounts[category.id] ?? -1) >= 0 ? `${categoryProductCounts[category.id]} products` : ''}
                              </Typography>
                              {category.showOnHomePage && (
                                <Chip icon={<HomeIcon />} label="Home" size="small" variant="outlined" sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: 9 }, '& .MuiChip-icon': { fontSize: 12 } }} />
                              )}
                              {category.showInShop && (
                                <Chip icon={<StorefrontIcon />} label="Shop" size="small" variant="outlined" sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: 9 }, '& .MuiChip-icon': { fontSize: 12 } }} />
                              )}
                            </Box>
                          </Box>
                          {(categoryProductCounts[category.id] ?? -1) >= 0 && (
                            <Chip label={categoryProductCounts[category.id]} size="small" variant="filled"
                              sx={{ height: 18, fontSize: '0.65rem', minWidth: 28, backgroundColor: '#1976d2', color: '#fff', '& .MuiChip-label': { color: '#fff' } }} />
                          )}
                          <Tooltip title="View products">
                            <IconButton size="small" onClick={() => handleViewCategoryProducts(category.id, category.name)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Add to Carousel">
                            <IconButton size="small" color="info" onClick={() => handleToggleCategoryVisibility(category.id, 'showInCarousel')}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
                </Collapse>
              </Box>
            )}
          </Box>
        )}

        {/* Shop Tab Content */}
        {activeTab === 'shop' && (
          <Box>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #66bb6a 0%, #43a047 100%)',
                boxShadow: '0 4px 12px rgba(102, 187, 106, 0.25)',
              }}
            >
              <StorefrontIcon sx={{ color: 'white', fontSize: 20 }} />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  color: 'white',
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  flex: 1,
                }}
              >
                Shop Page Categories ({shopCategories.length})
              </Typography>
              <Tooltip title='Categories with "Show in Shop" enabled'>
                <InfoIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 18 }} />
              </Tooltip>
            </Box>

            {/* Description */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 1 }}>
              Drag to reorder category icons in the shop page grid.
              Position changes are independent from Home Order and Carousel.
            </Typography>

            {/* Draggable Category List */}
            {filteredShopCategories.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <StorefrontIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {isSearchActive ? 'No categories match your search.' : 'No categories enabled for shop page yet.'}
                </Typography>
                {!isSearchActive && (
                  <Typography variant="caption" color="text.secondary">
                    Categories come from Vend.
                  </Typography>
                )}
              </Box>
            ) : (
              <DragDropContext onDragStart={handleDragStart} onDragEnd={handleCategoryDragEnd}>
                <Droppable droppableId="shop-categories">
                  {(provided: DroppableProvided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {filteredShopCategories.map((category, index) => (
                        <Draggable
                          key={category.id}
                          draggableId={`shop-${category.id}`}
                          index={index}
                          isDragDisabled={isSearchActive}
                        >
                          {(dragProvided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
                            const productCount = categoryProductCounts[category.id];
                            const hasNoProducts = !isAllLocations && productCount === 0;
                            const isLocked = category.lockedGlobalPosition !== null && category.lockedGlobalPosition !== undefined;
                            return (
                              <PortalAwareDraggable provided={dragProvided} snapshot={snapshot}>
                                <Card
                                  variant="outlined"
                                  sx={{
                                    opacity: hasNoProducts ? 0.5 : 1,
                                    transition: snapshot.isDragging ? 'none' : 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    cursor: 'default',
                                    border: '1px solid',
                                    borderColor: snapshot.isDragging ? 'success.main' : hasNoProducts ? 'warning.light' : 'divider',
                                    background: snapshot.isDragging
                                      ? alpha('#66bb6a', 0.04)
                                      : hasNoProducts
                                        ? alpha('#ff9800', 0.04)
                                        : 'background.paper',
                                    '&:hover': snapshot.isDragging ? {} : {
                                      borderColor: hasNoProducts ? 'warning.main' : 'success.light',
                                      boxShadow: hasNoProducts
                                        ? '0 4px 12px rgba(255, 152, 0, 0.12)'
                                        : '0 4px 12px rgba(102, 187, 106, 0.12)',
                                      transform: 'translateY(-2px)',
                                    },
                                  }}
                                >
                                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                      {/* Drag handle */}
                                      <Box
                                        {...dragProvided.dragHandleProps}
                                        sx={{
                                          cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                                          display: 'flex',
                                          alignItems: 'center',
                                          color: snapshot.isDragging ? 'success.main' : 'action.active',
                                        }}
                                      >
                                        <DragIndicatorIcon />
                                      </Box>

                                      {/* Position number */}
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          width: 24,
                                          height: 24,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderRadius: '50%',
                                          bgcolor: 'success.main',
                                          color: 'white',
                                          fontWeight: 600,
                                        }}
                                      >
                                        {index + 1}
                                      </Typography>

                                      {/* Category image */}
                                      <Box
                                        sx={{
                                          width: 48,
                                          height: 48,
                                          backgroundColor: '#FF002C',
                                          borderRadius: 1,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          overflow: 'hidden',
                                          flexShrink: 0,
                                        }}
                                      >
                                        {category.imageUrl ? (
                                          <img
                                            src={category.imageUrl}
                                            alt={category.name}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                          />
                                        ) : (
                                          <CategoryIcon sx={{ fontSize: 24, color: 'rgba(255,255,255,0.7)' }} />
                                        )}
                                      </Box>

                                      {/* Category info */}
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="body2" fontWeight={500} noWrap>
                                            {category.name}
                                          </Typography>
                                          {!isAllLocations && productCount !== undefined && (
                                            <Chip
                                              label={productCount}
                                              size="small"
                                              variant="filled"
                                              sx={{
                                                height: 18,
                                                fontSize: '0.65rem',
                                                minWidth: 28,
                                                backgroundColor: productCount > 0 ? '#2E7D32' : '#ED6C02',
                                                color: '#FFFFFF',
                                                '& .MuiChip-label': { color: '#FFFFFF' },
                                              }}
                                            />
                                          )}
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
                                          <Chip
                                            icon={<StorefrontIcon />}
                                            label="Shop"
                                            size="small"
                                            color="primary"
                                            sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: 9 }, '& .MuiChip-icon': { fontSize: 12 } }}
                                          />
                                          {category.showTagFilters && (
                                            <Chip
                                              icon={<FilterListIcon />}
                                              label="Filters"
                                              size="small"
                                              color="secondary"
                                              variant="outlined"
                                              sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: 9 }, '& .MuiChip-icon': { fontSize: 12 } }}
                                            />
                                          )}
                                          {!isAllLocations && hasNoProducts && (
                                            <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.6rem' }}>
                                              No products
                                            </Typography>
                                          )}
                                        </Box>
                                      </Box>

                                      {/* Lock position button */}
                                      <Tooltip title={isLocked ? `Locked at position ${category.lockedGlobalPosition} (click to unlock)` : `Lock position ${index + 1} across all screens`}>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleToggleLockPosition(category.id, index + 1);
                                          }}
                                          sx={{
                                            color: isLocked ? 'warning.main' : 'action.disabled',
                                          }}
                                        >
                                          {isLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                                        </IconButton>
                                      </Tooltip>

                                      {/* View products button - hidden in All Locations mode */}
                                      {!isAllLocations && (
                                        <Tooltip title="View products">
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleViewCategoryProducts(category.id, category.name);
                                            }}
                                          >
                                            <VisibilityIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}

                                    {/* Edit removed — categories are edited in
                                        the All Categories tab, against the draft. */}
                                    </Box>
                                  </CardContent>
                                </Card>
                              </PortalAwareDraggable>
                            );
                          }}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}

            {/* Hidden shop categories section (only when groupEmptyAtBottom is ON) */}
            {/* Search active hint */}
            {isSearchActive && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                Clear search to reorder
              </Typography>
            )}

            {filteredHiddenShopCategories.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Box
                  onClick={() => setEmptyCollapsed(!emptyCollapsed)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: alpha('#ff9800', 0.06),
                    border: '1px dashed',
                    borderColor: 'warning.light',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha('#ff9800', 0.1) },
                  }}
                >
                  <VisibilityOffIcon fontSize="small" sx={{ color: 'warning.main' }} />
                  <Typography variant="caption" color="warning.main" fontWeight={600} sx={{ flex: 1 }}>
                    {filteredHiddenShopCategories.length} empty at {selectedStore?.name || 'this store'}
                  </Typography>
                  {emptyCollapsed ? <ExpandMoreIcon fontSize="small" sx={{ color: 'warning.main' }} /> : <ExpandLessIcon fontSize="small" sx={{ color: 'warning.main' }} />}
                </Box>
                <Collapse in={!emptyCollapsed}>
                <Box sx={{ mt: 1 }}>
                  {filteredHiddenShopCategories.map((category) => (
                    <Card
                      key={category.id}
                      variant="outlined"
                      sx={{
                        opacity: 0.5,
                        mb: 0.5,
                        border: '1px solid',
                        borderColor: 'warning.light',
                        background: alpha('#ff9800', 0.04),
                      }}
                    >
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <VisibilityOffIcon fontSize="small" sx={{ color: 'warning.main' }} />
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              backgroundColor: '#FF002C',
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              flexShrink: 0,
                            }}
                          >
                            {category.imageUrl ? (
                              <img
                                src={category.imageUrl}
                                alt={category.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <CategoryIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.7)' }} />
                            )}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={500} noWrap>
                              {category.name}
                            </Typography>
                            <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.6rem' }}>
                              No products at this store
                            </Typography>
                          </Box>
                          <Chip
                            label={0}
                            size="small"
                            variant="filled"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              minWidth: 28,
                              backgroundColor: '#ED6C02',
                              color: '#FFFFFF',
                            }}
                          />
                          <Tooltip title="View products at other stores">
                            <IconButton
                              size="small"
                              onClick={() => handleViewCategoryProducts(category.id, category.name)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
                </Collapse>
              </Box>
            )}

            {/* Available to add: active, but the Shop tab tick is off */}
            {availableShopCategories.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Box
                  onClick={() => setAvailableCollapsed(!availableCollapsed)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 1, py: 0.5, borderRadius: 1,
                    bgcolor: alpha('#1976d2', 0.06),
                    border: '1px dashed', borderColor: 'info.light',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha('#1976d2', 0.1) },
                  }}
                >
                  <AddIcon fontSize="small" sx={{ color: 'info.main' }} />
                  <Typography variant="caption" color="info.main" fontWeight={600} sx={{ flex: 1 }}>
                    {availableShopCategories.length} available to add
                  </Typography>
                  {availableCollapsed ? <ExpandMoreIcon fontSize="small" sx={{ color: 'info.main' }} /> : <ExpandLessIcon fontSize="small" sx={{ color: 'info.main' }} />}
                </Box>
                <Collapse in={!availableCollapsed}>
                <Box sx={{ mt: 1 }}>
                  {availableShopCategories.map((category) => (
                    <Card
                      key={category.id}
                      variant="outlined"
                      sx={{
                        opacity: 0.7, mb: 0.5,
                        border: '1px solid', borderColor: 'info.light',
                        background: alpha('#1976d2', 0.03),
                        '&:hover': { opacity: 1, borderColor: 'info.main' },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ width: 36, height: 36, backgroundColor: '#FF002C', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                            {category.imageUrl ? (
                              <img src={category.imageUrl} alt={category.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <CategoryIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.7)' }} />
                            )}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={500} noWrap>{category.name}</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, flexWrap: 'wrap', alignItems: 'center' }}>
                              <Typography variant="caption" color="info.main" sx={{ fontSize: '0.6rem', mr: 0.5 }}>
                                {(categoryProductCounts[category.id] ?? -1) >= 0 ? `${categoryProductCounts[category.id]} products` : ''}
                              </Typography>
                              {category.showOnHomePage && (
                                <Chip icon={<HomeIcon />} label="Home" size="small" variant="outlined" sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: 9 }, '& .MuiChip-icon': { fontSize: 12 } }} />
                              )}
                              {category.showInCarousel && (
                                <Chip icon={<ViewCarouselIcon />} label="Carousel" size="small" variant="outlined" sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: 9 }, '& .MuiChip-icon': { fontSize: 12 } }} />
                              )}
                            </Box>
                          </Box>
                          {(categoryProductCounts[category.id] ?? -1) >= 0 && (
                            <Chip label={categoryProductCounts[category.id]} size="small" variant="filled"
                              sx={{ height: 18, fontSize: '0.65rem', minWidth: 28, backgroundColor: '#1976d2', color: '#fff', '& .MuiChip-label': { color: '#fff' } }} />
                          )}
                          <Tooltip title="View products">
                            <IconButton size="small" onClick={() => handleViewCategoryProducts(category.id, category.name)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Add to Shop">
                            <IconButton size="small" color="info" onClick={() => handleToggleCategoryVisibility(category.id, 'showInShop')}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
                </Collapse>
              </Box>
            )}
          </Box>
        )}

        {/* Brands Tab Content */}
        {activeTab === 'brands' && (
          <Box>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #ab47bc 0%, #8e24aa 100%)',
                boxShadow: '0 4px 12px rgba(171, 71, 188, 0.25)',
              }}
            >
              <BrandsIcon sx={{ color: 'white', fontSize: 20 }} />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  color: 'white',
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  flex: 1,
                }}
              >
                Brand Order ({allBrands.length})
              </Typography>
              {isSaving && (
                <CircularProgress size={16} sx={{ color: 'rgba(255,255,255,0.7)' }} />
              )}
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setBrandForm({ brand: null })}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.18)',
                  color: 'white',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.28)', boxShadow: 'none' },
                }}
              >
                Add brand
              </Button>
              <Tooltip title="Drag brands to set the display order on the mobile app and web. A brand added here goes to the end of the rail.">
                <InfoIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 18 }} />
              </Tooltip>
            </Box>

            {/* Description */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 1 }}>
              Drag to reorder. Order is saved to draft and published together with other changes —
              adding, editing and deleting a brand take effect immediately.
            </Typography>

            {/* Loading state */}
            {brandsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            )}

            {!brandsLoading && (
              <>
                {/* Search */}
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search brands..."
                  value={brandSearch}
                  onChange={(e) => setBrandSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: brandSearch && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setBrandSearch('')}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                {brandSearch.trim() ? (
                  /* Search results — flat list, no drag (can't reorder a filtered subset) */
                  <Box
                    sx={{
                      maxHeight: 600,
                      overflowY: 'auto',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    {filteredBrands.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          No brands match your search
                        </Typography>
                      </Box>
                    ) : (
                      filteredBrands.map((brand, index) => (
                        <Box
                          key={brand.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            p: 1,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 'none' },
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              width: 24,
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '50%',
                              bgcolor: 'grey.200',
                              color: 'text.secondary',
                              fontWeight: 600,
                              fontSize: 11,
                              flexShrink: 0,
                            }}
                          >
                            {allBrands.findIndex((b) => b.id === brand.id) + 1}
                          </Typography>
                          <BrandLogoPlate imageUrl={brand.imageUrl} name={brand.name} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" noWrap sx={{ fontWeight: 600, fontSize: '0.92rem' }}>{brand.name}</Typography>
                              {!isAllLocations && brandProductCounts[brand.id] !== undefined && brandProductCounts[brand.id] !== -1 && (
                                <Chip
                                  label={brandProductCounts[brand.id]}
                                  size="small"
                                  variant="filled"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    minWidth: 28,
                                    backgroundColor: brandProductCounts[brand.id] > 0 ? '#2E7D32' : '#ED6C02',
                                    color: '#FFFFFF',
                                    '& .MuiChip-label': { color: '#FFFFFF' },
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                          {!isAllLocations && (
                            <Tooltip title="View products for this brand">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBrandProductsModal({ brandName: brand.name });
                                }}
                                sx={{
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    backgroundColor: alpha('#4caf50', 0.1),
                                    transform: 'scale(1.1)',
                                  },
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Edit name or logo">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); setBrandForm({ brand }); }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete brand">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); setBrandPendingDelete(brand); }}
                            >
                              <DeleteIcon fontSize="small" color="error" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))
                    )}
                  </Box>
                ) : (
                  /* Full list — draggable */
                  <DragDropContext onDragStart={handleDragStart} onDragEnd={handleBrandDragEnd}>
                    <Droppable droppableId="all-brands">
                      {(provided: DroppableProvided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {allBrands.map((brand, index) => (
                            <Draggable
                              key={brand.id}
                              draggableId={brand.id}
                              index={index}
                            >
                              {(dragProvided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
                                const brandHasNoProducts = !isAllLocations && brandProductCounts[brand.id] === 0;
                                return (
                                <PortalAwareDraggable provided={dragProvided} snapshot={snapshot}>
                                  <Card
                                    variant="outlined"
                                    sx={{
                                      opacity: brandHasNoProducts ? 0.5 : 1,
                                      transition: snapshot.isDragging ? 'none' : 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                      border: '1px solid',
                                      borderColor: snapshot.isDragging
                                        ? 'secondary.main'
                                        : brandHasNoProducts
                                          ? 'warning.light'
                                          : 'divider',
                                      background: snapshot.isDragging
                                        ? alpha('#ab47bc', 0.04)
                                        : brandHasNoProducts
                                          ? alpha('#ff9800', 0.04)
                                          : 'background.paper',
                                      '&:hover': snapshot.isDragging ? {} : {
                                        borderColor: brandHasNoProducts ? 'warning.main' : 'secondary.light',
                                        boxShadow: brandHasNoProducts
                                          ? '0 2px 8px rgba(255, 152, 0, 0.1)'
                                          : '0 2px 8px rgba(171, 71, 188, 0.1)',
                                      },
                                    }}
                                  >
                                    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        {/* Drag handle */}
                                        <Box
                                          {...dragProvided.dragHandleProps}
                                          sx={{
                                            cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: snapshot.isDragging ? 'secondary.main' : 'action.active',
                                            transition: 'color 0.2s ease',
                                          }}
                                        >
                                          <DragIndicatorIcon fontSize="small" />
                                        </Box>

                                        {/* Position badge */}
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            width: 24,
                                            height: 24,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '50%',
                                            bgcolor: 'secondary.main',
                                            color: 'secondary.contrastText', // secondary.main is the text colour: white digits vanished on dark
                                            fontWeight: 600,
                                            fontSize: 11,
                                            flexShrink: 0,
                                          }}
                                        >
                                          {index + 1}
                                        </Typography>

                                        {/* Brand image */}
                                        <BrandLogoPlate imageUrl={brand.imageUrl} name={brand.name} />

                                        {/* Brand info */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" noWrap sx={{ fontWeight: 600, fontSize: '0.92rem' }}>
                                              {brand.name}
                                            </Typography>
                                            {!isAllLocations && brandProductCounts[brand.id] !== undefined && brandProductCounts[brand.id] !== -1 && (
                                              <Chip
                                                label={brandProductCounts[brand.id]}
                                                size="small"
                                                variant="filled"
                                                sx={{
                                                  height: 18,
                                                  fontSize: '0.65rem',
                                                  minWidth: 28,
                                                  backgroundColor: brandProductCounts[brand.id] > 0 ? '#2E7D32' : '#ED6C02',
                                                  color: '#FFFFFF',
                                                  '& .MuiChip-label': { color: '#FFFFFF' },
                                                }}
                                              />
                                            )}
                                          </Box>
                                          {!isAllLocations && brandProductCounts[brand.id] === 0 && (
                                            <Typography variant="caption" color="text.secondary">No products at this store</Typography>
                                          )}
                                        </Box>

                                        {/* View products button - hidden in All Locations mode */}
                                        {!isAllLocations && (
                                          <Tooltip title="View products for this brand">
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setBrandProductsModal({ brandName: brand.name });
                                              }}
                                              sx={{
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                  backgroundColor: alpha('#4caf50', 0.1),
                                                  transform: 'scale(1.1)',
                                                },
                                              }}
                                            >
                                              <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}

                                        <Tooltip title="Edit name or logo">
                                          <IconButton
                                            size="small"
                                            onClick={(e) => { e.stopPropagation(); setBrandForm({ brand }); }}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Delete brand">
                                          <IconButton
                                            size="small"
                                            onClick={(e) => { e.stopPropagation(); setBrandPendingDelete(brand); }}
                                          >
                                            <DeleteIcon fontSize="small" color="error" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </CardContent>
                                  </Card>
                                </PortalAwareDraggable>
                              );
                              }}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </>
            )}
          </Box>
        )}

        {/* All Categories Tab Content */}
        {activeTab === 'all' && (
          <AllCategoriesTab
            categories={draft?.categoryConfig?.categories || []}
            onCategoryUpdate={handleCategoryUpdateFromAllTab}
            onViewProducts={handleViewCategoryProducts}
            isSaving={isSaving}
            onCategoriesChanged={refreshDraft}
          />
        )}
      </Box>
      {/* End Scrollable Content Area */}

      {/* The category editor used to live here too, opened from the Home,
          Carousel and Shop tabs. It wrote LIVE (PATCH /admin/categories/:id),
          bypassing the draft workflow it was sitting inside — so the same
          category could be edited two ways with two different write paths.
          Category editing is now only in the All Categories tab, against the
          draft. These tabs arrange categories; they no longer edit them. */}

      {/* New & Popular Config Dialog */}
      <NewPopularConfigDialog
        open={newPopularDialogOpen}
        onClose={() => setNewPopularDialogOpen(false)}
        config={draft?.categoryConfig?.newPopularConfig || {
          homeFilterType: 'newOrPopular',
          homeFilterValue: null,
          name: 'New & Popular',
        }}
        onSave={handleNewPopularSave}
      />

      {/* Banner Item Edit Dialog */}
      {currentBannerPosition && (
        <BannerItemFormDialog
          open={bannerDialogOpen}
          onClose={handleBannerDialogClose}
          bannerId={currentBannerPosition.bannerId || ''}
          bannerName={currentBannerPosition.label}
          item={editingBannerItem ? {
            id: editingBannerItem.id,
            name: editingBannerItem.name,
            image: editingBannerItem.image,
            imageUrl: editingBannerItem.imageUrl,
            redirectUrl: editingBannerItem.redirectUrl || '',
            isActive: editingBannerItem.isActive,
            linkType: editingBannerItem.linkType,
            linkedEntityId: editingBannerItem.linkedEntityId,
          } : null}
          onSuccess={handleBannerDialogClose}
          onDraftSave={handleBannerDraftSave}
        />
      )}

      {/* Banner Delete Confirmation Dialog */}
      <Dialog
        open={deleteBannerConfirmOpen}
        onClose={() => {
          setDeleteBannerConfirmOpen(false);
          setDeleteBannerTarget(null);
        }}
      >
        <DialogTitle>Delete Banner Item</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove <strong>{deleteBannerTarget?.item.name || 'this banner'}</strong>?
            {deleteBannerTarget?.item.id.startsWith('draft_')
              ? ' This item has not been published yet.'
              : ' This change will take effect when you publish.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteBannerConfirmOpen(false);
            setDeleteBannerTarget(null);
          }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDeleteBannerItem} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lock Position Conflict Dialog */}
      <Dialog
        open={lockConflictDialog?.open ?? false}
        onClose={() => setLockConflictDialog(null)}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon color="warning" />
          Position Already Locked
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Position <strong>{lockConflictDialog?.position}</strong> is already locked by <strong>{lockConflictDialog?.conflictingCategoryName}</strong>.
            <br /><br />
            Do you want to unlock <strong>{lockConflictDialog?.conflictingCategoryName}</strong> and lock <strong>{lockConflictDialog?.categoryName}</strong> at this position instead?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLockConflictDialog(null)}>
            Cancel
          </Button>
          <Button onClick={handleForceLock} color="warning" variant="contained">
            Replace Lock
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drag Lock Conflict Dialog - when dragging a locked category to a position occupied by another locked category */}
      <Dialog
        open={dragLockConflictDialog?.open ?? false}
        onClose={() => setDragLockConflictDialog(null)}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon color="warning" />
          Cannot Move to Locked Position
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Position <strong>{dragLockConflictDialog?.targetPosition}</strong> is already locked by <strong>{dragLockConflictDialog?.conflictingCategoryName}</strong>.
            <br /><br />
            To move <strong>{dragLockConflictDialog?.categoryName}</strong> to position {dragLockConflictDialog?.targetPosition}, you must unlock <strong>{dragLockConflictDialog?.conflictingCategoryName}</strong> first.
            <br /><br />
            Do you want to unlock <strong>{dragLockConflictDialog?.conflictingCategoryName}</strong> and move <strong>{dragLockConflictDialog?.categoryName}</strong> to this position?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDragLockConflictDialog(null)}>
            Cancel
          </Button>
          <Button onClick={handleForceDragLockedCategory} color="warning" variant="contained">
            Unlock &amp; Move
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lock All Confirmation Dialog */}
      <Dialog
        open={lockAllDialog?.open ?? false}
        onClose={() => setLockAllDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon color="secondary" />
          Lock All Category Positions
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {lockAllDialog?.isAllLocations ? (
              <>
                <Typography variant="body2" color="warning.main" sx={{ mb: 2, fontWeight: 500 }}>
                  ⚠️ All Locations Mode - Global Lock
                </Typography>
                <Typography variant="body2" paragraph>
                  This will lock <strong>{lockAllDialog?.categoryCount} active categories</strong> at their current positions.
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Important:</strong> This action will:
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <li>
                    <Typography variant="body2">Make all active categories visible on <strong>all tabs</strong> (Home, Carousel, Shop)</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Synchronize positions across all tabs to match the current order</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Lock each category at its unified position</Typography>
                  </li>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  After locking, dragging any category will move it to the same position on all tabs.
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2" paragraph>
                  This will lock <strong>{lockAllDialog?.categoryCount} visible categories</strong> on the current tab at their current positions.
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>What happens:</strong>
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <li>
                    <Typography variant="body2">Positions will be synchronized across all tabs</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Each category will be locked at its unified position</Typography>
                  </li>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  After locking, dragging any locked category will move it to the same position on all tabs.
                </Typography>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLockAllDialog(null)}>
            Cancel
          </Button>
          <Button onClick={handleConfirmLockAll} color="secondary" variant="contained" startIcon={<LockIcon />}>
            Lock All Positions
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Products Modal */}
      {productsModalCategory && (
        <CategoryProductsModal
          open={productsModalOpen}
          onClose={handleCloseProductsModal}
          categoryId={productsModalCategory.id}
          categoryName={productsModalCategory.name}
          initialStoreId={isAllLocations ? undefined : selectedStoreId || undefined}
        />
      )}

      {/* Brand Products Modal */}
      {brandProductsModal && (
        <BrandProductsModal
          open={!!brandProductsModal}
          onClose={() => setBrandProductsModal(null)}
          brandName={brandProductsModal.brandName}
          initialStoreId={isAllLocations ? undefined : selectedStoreId || undefined}
        />
      )}

      {/* Add / edit a brand (name + logo) */}
      <BrandFormDialog
        open={!!brandForm}
        brand={brandForm?.brand ?? null}
        onClose={() => setBrandForm(null)}
        onSaved={handleBrandSaved}
      />

      {/* Delete confirmation */}
      <Dialog
        open={!!brandPendingDelete}
        onClose={brandDeleting ? undefined : () => setBrandPendingDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete {brandPendingDelete?.name}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            The brand disappears from the rail in the app. Products keep their brand name, so
            nothing in the catalogue is lost — you can add the brand again later.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBrandPendingDelete(null)} disabled={brandDeleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBrandDelete}
            disabled={brandDeleting}
            startIcon={brandDeleting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
});
